import type { Translation } from '../../types';
import type { domains as en } from '../en/domains';

export const domains: Translation<typeof en> = {
  status: {
    lifecycle: {
      DRAFT: 'Nháp',
      AWAITING_DOSSIER: 'Chờ hồ sơ',
      AWAITING_PAYMENT: 'Chờ thanh toán',
      PENDING_CREATE: 'Đang đăng ký',
      CREATE_FAILED: 'Đăng ký thất bại',
      ACTIVE: 'Hoạt động',
      PENDING_RENEW: 'Đang gia hạn',
      PENDING_TRANSFER: 'Đang chuyển nhà đăng ký',
      EXPIRED: 'Hết hạn',
      GRACE_PERIOD: 'Thời gian ân hạn',
      REDEMPTION: 'Chuộc lại',
      PENDING_DELETE: 'Chờ xoá',
      DELETED: 'Đã xoá',
      TRANSFERRED_OUT: 'Đã chuyển đi',
    },
    compliance: {
      UNKNOWN: 'Chưa xác định',
      COMPLIANT: 'Tuân thủ',
      ACTION_REQUIRED: 'Cần xử lý',
      BLOCKED: 'Bị chặn',
      SUSPENDED: 'Tạm ngưng',
    },
    service: {
      NORMAL: 'Bình thường',
      DNS_SUSPENDED: 'Tạm ngưng DNS',
      SUSPENDED: 'Tạm ngưng',
      LOCKED: 'Bị khoá',
    },
    billing: {
      NONE: 'Không có',
      AWAITING_PAYMENT: 'Chờ thanh toán',
      RESERVED: 'Đã tạm giữ tiền',
      PAID: 'Đã thanh toán',
      REFUND_PENDING: 'Chờ hoàn tiền',
      REFUNDED: 'Đã hoàn tiền',
      OVERDUE: 'Quá hạn thanh toán',
    },
    dossier: {
      NOT_CREATED: 'Chưa tạo',
      COLLECTING: 'Đang thu thập',
      PENDING_IDENTITY: 'Chờ định danh',
      PENDING_DOCUMENT: 'Chờ tài liệu',
      PENDING_SIGNATURE: 'Chờ ký',
      PENDING_REVIEW: 'Chờ duyệt',
      COMPLETE: 'Hoàn tất',
      INVALID: 'Không hợp lệ',
      EXPIRED: 'Hết hạn',
      CORRECTION_REQUIRED: 'Cần bổ sung, sửa đổi',
      SUPERSEDED: 'Đã thay thế',
    },
    command: {
      PENDING: 'Chờ gửi',
      SENT: 'Đã gửi',
      SUCCESS: 'Thành công',
      REJECTED: 'Bị từ chối',
      UNKNOWN: 'Chưa rõ kết quả',
      RECONCILING: 'Đang đối soát',
      RETRY_PENDING: 'Chờ thử lại',
      MANUAL_REVIEW: 'Cần xem xét thủ công',
    },
    policy: {
      ALLOW: 'Cho phép',
      ALLOW_WITH_REQUIRED_ACTIONS: 'Cho phép kèm yêu cầu',
      BLOCK: 'Chặn',
    },
    workflow: {
      RUNNING: 'Đang chạy',
      WAITING: 'Đang chờ',
      COMPLETED: 'Hoàn tất',
      FAILED: 'Thất bại',
      CANCELLED: 'Đã huỷ',
      MANUAL_REVIEW: 'Cần xem xét thủ công',
      COMPENSATING: 'Đang hoàn tác',
    },
    transfer: {
      REQUESTED: 'Đã yêu cầu',
      AUTHORIZED: 'Đã xác nhận',
      REGISTRY_PENDING: 'Chờ registry xử lý',
      APPROVED: 'Đã chấp thuận',
      REJECTED: 'Bị từ chối',
      CANCELLED: 'Đã huỷ',
      EXPIRED: 'Hết hạn',
      COMPLETED: 'Hoàn tất',
    },
    abuse: {
      NEW: 'Mới',
      TRIAGED: 'Đã phân loại',
      INVESTIGATING: 'Đang điều tra',
      ACTION_REQUIRED: 'Cần xử lý',
      WAITING: 'Đang chờ',
      RESOLVED: 'Đã giải quyết',
      CLOSED: 'Đã đóng',
    },
    dispute: {
      OPENED: 'Đã mở',
      LOCKED: 'Đã khoá tên miền',
      DECIDED: 'Đã có phán quyết',
      IMPLEMENTED: 'Đã thi hành phán quyết',
      WITHDRAWN: 'Đã rút',
      CLOSED: 'Đã đóng',
    },
    vnnicSync: {
      NOT_SYNCED: 'Chưa đồng bộ',
      SYNC_PENDING: 'Chờ đồng bộ',
      QUEUED: 'Đã xếp hàng',
      SUBMITTING: 'Đang gửi',
      SUBMITTED: 'Đã gửi, chờ VNNIC',
      ACKNOWLEDGED: 'VNNIC đã chấp nhận',
      REJECTED: 'VNNIC từ chối',
      CORRECTION_REQUIRED: 'VNNIC yêu cầu bổ sung',
      RESUBMIT_PENDING: 'Chờ gửi lại',
      SYNC_ERROR: 'Lỗi đồng bộ',
      SUPERSEDED: 'Đã có hồ sơ mới thay thế',
    },
    vnnicTx: {
      SUBMITTING: 'Đang gửi',
      SUBMITTED: 'Đã gửi',
      ACKNOWLEDGED: 'Được chấp nhận',
      REJECTED: 'Bị từ chối',
      CORRECTION_REQUIRED: 'Yêu cầu bổ sung',
      FAILED: 'Thất bại',
      UNKNOWN: 'Chưa rõ kết quả',
    },
    code: 'Mã: {{code}}',
  },

  vocab: {
    evidence: { SIGNATURE_CERT: 'Chữ ký số trên bản khai', DOCUMENT: 'Tài liệu', EKYC_RESULT: 'Kết quả eKYC', REVIEW_NOTE: 'Ghi chú của người duyệt', PROVIDER_RESULT: 'Kết quả của nhà cung cấp', EID_ASSERTION: 'Định danh điện tử' },
    evidenceField: { signer: 'Người ký', signed_at: 'Ngày ký', ca: 'Tổ chức cấp chứng thư', timestamp: 'Dấu thời gian', checks: 'Kiểm tra', ekyc_request_id: 'Mã yêu cầu eKYC', face_match_score: 'Độ khớp khuôn mặt', request_id: 'Mã yêu cầu', declaration_id: 'Bản khai' },
    eppStatus: {
      ok: 'Bình thường (ok)',
      inactive: 'Chưa hoạt động (chưa có máy chủ tên)',
      clientHold: 'Tạm ngưng phân giải (nhà đăng ký)',
      serverHold: 'Tạm ngưng phân giải (registry)',
      clientDeleteProhibited: 'Cấm xoá (nhà đăng ký)',
      serverDeleteProhibited: 'Cấm xoá (registry)',
      clientRenewProhibited: 'Chặn gia hạn lặp (24 giờ sau khi gia hạn)',
      serverRenewProhibited: 'Cấm gia hạn (registry)',
      clientTransferProhibited: 'Khoá chuyển nhà đăng ký',
      serverTransferProhibited: 'Cấm chuyển nhà đăng ký (registry)',
      clientUpdateProhibited: 'Khoá cập nhật (bảo vệ thông tin liên hệ)',
      serverUpdateProhibited: 'Cấm cập nhật (registry)',
      pendingCreate: 'Đang chờ tạo',
      pendingDelete: 'Đang chờ xoá',
      pendingRenew: 'Đang chờ gia hạn',
      pendingTransfer: 'Đang chờ chuyển nhà đăng ký',
      pendingUpdate: 'Đang chờ cập nhật',
      pendingRestore: 'Đang chờ khôi phục',
      redemptionPeriod: 'Thời gian chuộc lại',
      addPeriod: 'Thời gian ân hạn sau đăng ký',
      autoRenewPeriod: 'Thời gian ân hạn gia hạn tự động',
      renewPeriod: 'Thời gian ân hạn sau gia hạn',
      transferPeriod: 'Thời gian ân hạn sau chuyển',
    },
    role: { REGISTRANT: 'Chủ thể', ADMIN: 'Quản trị', TECH: 'Kỹ thuật', BILLING: 'Thanh toán' },
    direction: { IN: 'Chuyển đến', OUT: 'Chuyển đi' },
    actor: { OPERATOR: 'Nhân viên', CUSTOMER: 'Khách hàng', RESELLER: 'Đại lý', SYSTEM: 'Hệ thống', EXTERNAL: 'Bên ngoài', REGISTRY: 'Registry' },
    tldClass: { VN: 'Tên miền quốc gia Việt Nam', VN_CCTLD: 'Tên miền quốc gia Việt Nam', GTLD: 'Tên miền quốc tế (gTLD)', NEW_GTLD: 'gTLD mới', CCTLD: 'Tên miền quốc gia (ccTLD)', OTHER: 'Khác' },
    object: { DOMAIN: 'Tên miền', CONTACT: 'Liên hệ', HOST: 'Máy chủ tên', POLL: 'Thông báo poll' },
    workflow: {
      DOMAIN_REGISTRATION: 'Đăng ký tên miền',
      DOMAIN_RENEWAL: 'Gia hạn tên miền',
      DOMAIN_UPDATE: 'Cập nhật tên miền',
      DOMAIN_EXPIRY: 'Xử lý hết hạn',
      DOMAIN_TRANSFER_IN: 'Chuyển đến',
      DOMAIN_TRANSFER_OUT: 'Chuyển đi',
      DOMAIN_REGISTRANT_CHANGE: 'Đổi chủ thể',
      VNNIC_SYNC: 'Đồng bộ hồ sơ VNNIC',
      REGISTRY_POLL: 'Nhận thông báo registry',
    },
    lock: {
      TRANSFER_LOCK: 'Khoá chuyển nhà đăng ký',
      REGISTRAR_LOCK: 'Khoá của nhà đăng ký',
      COMPLIANCE_LOCK: 'Khoá tuân thủ',
      LEGAL_LOCK: 'Khoá pháp lý',
      DISPUTE_LOCK: 'Khoá tranh chấp',
      ABUSE_LOCK: 'Khoá do lạm dụng',
      UDRP_LOCK: 'Khoá theo thủ tục UDRP',
      COURT_LOCK: 'Khoá theo quyết định của toà án',
      REGISTRY_LOCK: 'Khoá tại registry',
    },
    documentType: {
      BUSINESS_REGISTRATION: 'Giấy chứng nhận đăng ký doanh nghiệp',
      NATIONAL_ID_SCAN: 'Bản chụp giấy tờ tuỳ thân',
      SIGNED_DECLARATION: 'Bản khai đã ký',
      EKYC_ID_CARD_FRONT: 'Ảnh eKYC mặt trước giấy tờ',
      EKYC_ID_CARD_BACK: 'Ảnh eKYC mặt sau giấy tờ',
      EKYC_SELFIE: 'Ảnh chân dung eKYC',
      NATIONAL_ID_CARD: 'Căn cước công dân',
      PASSPORT: 'Hộ chiếu',
      BUSINESS_REGISTRATION_CERTIFICATE: 'Giấy chứng nhận đăng ký doanh nghiệp',
      REGISTRATION_DECLARATION: 'Bản khai đăng ký tên miền',
      ADDRESS_PROOF: 'Giấy tờ chứng minh địa chỉ',
      AUTHORIZATION_LETTER: 'Giấy uỷ quyền',
      REGISTRAR_SIGNED_FILE: 'Tệp NĐK đã ký',
      REGISTRAR_SIGNED_DECLARATION: 'Bản khai NĐK đã ký',
      SIGNED_SUBJECT_DECLARATION: 'Bản khai thông tin chủ thể đã ký',
      REGISTRATION_FORM: 'Bản khai tên miền',
      DOMAIN_DECLARATION: 'Bản khai tên miền',
      TRANSFEREE_DECLARATION: 'Bản khai của bên nhận chuyển nhượng',
      NEW_HOLDER_DECLARATION: 'Bản khai của chủ thể mới',
      IDENTITY_CARD_FRONT: 'CCCD mặt trước',
      IDENTITY_CARD_BACK: 'CCCD mặt sau',
      REGISTRAR_CHANGE_FORM: 'Bản khai chuyển đổi nhà đăng ký',
      CHANGE_INFORMATION_REQUEST: 'Đơn đề nghị thay đổi thông tin tên miền',
      SENSITIVE_KEYWORD_COMMITMENT: 'Cam kết tên miền chứa từ khoá nhạy cảm',
      RETURN_REQUEST: 'Đơn đề nghị hoàn trả tên miền',
      RETURN_REQUEST_FORM: 'Đơn đề nghị hoàn trả tên miền (mẫu)',
      GOV_VN_COMMITMENT: 'Biên bản giải trình và cam kết gov.vn',
      LOOKING_DOMAIN_FORM: 'Bản khai dịch vụ looking domain',
      NAME_VN_EXPLANATION: 'Bản giải trình đăng ký .name.vn',
      SIGNED_ORGANIZATION_INFO: 'Bản khai thông tin tổ chức đã ký',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Đơn đề nghị thay đổi tên chủ thể',
      NAME_CHANGE_DECISION: 'Quyết định đổi tên',
      TRANSFER_TAX_COMMITMENT: 'Cam kết nghĩa vụ thuế chuyển nhượng',
      TRANSFER_TAX_RECEIPT: 'Chứng từ nộp thuế chuyển nhượng',
      TRANSFER_AGREEMENT: 'Văn bản đề nghị chuyển nhượng tên miền',
      TRANSFER_APPROVAL: 'Văn bản chấp thuận chuyển nhượng tên miền',
      DOMAIN_CERTIFICATE: 'Giấy chứng nhận tên miền',
      SERVICE_CONTRACT: 'Hợp đồng cung cấp dịch vụ tên miền',
      OTHER: 'Khác',
    },
    purpose: {
      REGISTRATION_FORM: 'Bản khai đăng ký tên miền',
      DOMAIN_DECLARATION: 'Bản khai tạo cho tên miền',
      IDENTITY_CARD_FRONT: 'Giấy tờ tuỳ thân (mặt trước)',
      IDENTITY_CARD_BACK: 'Giấy tờ tuỳ thân (mặt sau)',
      EKYC_PORTRAIT: 'Ảnh chân dung eKYC',
      BUSINESS_REGISTRATION: 'Giấy chứng nhận đăng ký doanh nghiệp',
      REGISTRATION_DECLARATION: 'Bản khai đăng ký',
      IDENTITY_DOCUMENT: 'Giấy tờ định danh',
      IDENTITY_PROOF: 'Chứng minh danh tính',
      ADDRESS_PROOF: 'Chứng minh địa chỉ',
      AUTHORIZATION: 'Uỷ quyền',
      REGISTRAR_SIGNED_FILE: 'Tệp NĐK đã ký',
      REGISTRAR_SIGNED_DECLARATION: 'Bản khai NĐK đã ký',
      SIGNED_DECLARATION: 'Bản khai đã ký',
      SIGNED_SUBJECT_DECLARATION: 'Bản khai thông tin chủ thể đã ký',
      NATIONAL_ID_SCAN: 'Bản chụp giấy tờ tuỳ thân',
      EKYC_ID_CARD_FRONT: 'Ảnh eKYC mặt trước giấy tờ',
      EKYC_ID_CARD_BACK: 'Ảnh eKYC mặt sau giấy tờ',
      EKYC_SELFIE: 'Ảnh chân dung eKYC',
      TRANSFEREE_DECLARATION: 'Bản khai của bên nhận chuyển nhượng',
      NEW_HOLDER_DECLARATION: 'Bản khai của chủ thể mới',
      REGISTRAR_CHANGE_FORM: 'Bản khai chuyển đổi nhà đăng ký',
      CHANGE_INFORMATION_REQUEST: 'Đơn đề nghị thay đổi thông tin tên miền',
      SENSITIVE_KEYWORD_COMMITMENT: 'Cam kết tên miền chứa từ khoá nhạy cảm',
      RETURN_REQUEST: 'Đơn đề nghị hoàn trả tên miền',
      RETURN_REQUEST_FORM: 'Đơn đề nghị hoàn trả tên miền (mẫu)',
      GOV_VN_COMMITMENT: 'Biên bản giải trình và cam kết gov.vn',
      LOOKING_DOMAIN_FORM: 'Bản khai dịch vụ looking domain',
      NAME_VN_EXPLANATION: 'Bản giải trình đăng ký .name.vn',
      SIGNED_ORGANIZATION_INFO: 'Bản khai thông tin tổ chức đã ký',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Đơn đề nghị thay đổi tên chủ thể',
      NAME_CHANGE_DECISION: 'Quyết định đổi tên',
      TRANSFER_TAX_COMMITMENT: 'Cam kết nghĩa vụ thuế chuyển nhượng',
      TRANSFER_TAX_RECEIPT: 'Chứng từ nộp thuế chuyển nhượng',
      TRANSFER_AGREEMENT: 'Văn bản đề nghị chuyển nhượng tên miền',
      TRANSFER_APPROVAL: 'Văn bản chấp thuận chuyển nhượng tên miền',
      DOMAIN_CERTIFICATE: 'Giấy chứng nhận tên miền',
      SERVICE_CONTRACT: 'Hợp đồng cung cấp dịch vụ tên miền',
      PASSPORT: 'Hộ chiếu',
      OTHER: 'Khác',
    },
    dossierType: {
      VN_DOMAIN: 'Tên miền .vn',
      INTERNATIONAL_GTLD: 'Tên miền quốc tế (gTLD)',
      INTERNATIONAL_NEW_GTLD: 'Tên miền quốc tế mới (new gTLD)',
      INTERNATIONAL_CCTLD: 'Tên miền quốc gia khác (ccTLD)',
      OTHER: 'Khác',
    },
    classification: { HIGHLY_SENSITIVE: 'Rất nhạy cảm', CONFIDENTIAL: 'Bảo mật', INTERNAL: 'Nội bộ', PUBLIC: 'Công khai' },
    check: { PASSED: 'Đạt', PENDING: 'Chờ kiểm tra', FAILED: 'Không đạt', REJECTED: 'Bị từ chối', VERIFIED: 'Đã xác minh', EXPIRED: 'Hết hạn' },
    method: {
      AUTH_INFO: 'Mã AuthInfo',
      EMAIL_LINK: 'Liên kết xác nhận qua e-mail',
      REGISTRY_NOTICE: 'Thông báo từ registry',
      DUAL_CONFIRMATION: 'Hai bên chủ thể cùng xác nhận',
      BUSINESS_REGISTRY_LOOKUP: 'Tra cứu cơ sở dữ liệu doanh nghiệp',
      EKYC_CCCD_CHIP: 'eKYC bằng CCCD gắn chip',
      MANUAL_REVIEW: 'Duyệt thủ công (đã bỏ)',
      DIGITAL_SIGNATURE: 'Chữ ký số',
      ELECTRONIC_IDENTITY: 'Định danh điện tử (VNeID)',
      EKYC: 'eKYC',
      LEGAL_DOCUMENT: 'Giấy tờ pháp lý (đã bỏ)',
      SIGNED_DOCUMENT: 'Văn bản có chữ ký',
      IN_PERSON: 'Trực tiếp',
      PHONE_CALL: 'Gọi điện',
    },
    channel: { EPP_POLL: 'Thông báo poll EPP', VNNIC: 'VNNIC', PORTAL: 'Cổng khách hàng', CONTROL_CENTER: 'Control Center' },
    failure: {
      NEW_REGISTRANT_VERIFICATION_FAILED: 'Không xác minh được chủ thể mới',
      CANCELLED_BY_CUSTOMER: 'Khách hàng đã huỷ',
      CONFIRMATION_EXPIRED: 'Quá hạn mà chưa có xác nhận',
      REGISTRY_REJECTED: 'Registry từ chối',
      // A transfer in (crates/lifecycle/src/transfer.rs)
      COMPLIANCE_BLOCKED: 'Bị chặn bởi kiểm tra tuân thủ',
      TRANSFER_FAILED: 'Chuyển về không thành công',
      TRANSFER_CANCELLED: 'Yêu cầu chuyển về đã bị huỷ',
      TRANSFER_REJECTED: 'Nhà đăng ký đang quản lý đã từ chối chuyển',
      // Registration, renewal, restore (crates/lifecycle): also the reason of the order item's refund
      FAILED: 'Xử lý không thành công',
      ABANDONED: 'Đã dừng xử lý',
      CANCELLED_BY_STAFF: 'Nhân viên đã huỷ',
      REJECTED_BY_STAFF: 'Nhân viên đã từ chối',
      NO_DOSSIER: 'Chưa có hồ sơ tên miền',
      DOSSIER_INCOMPLETE: 'Hồ sơ tên miền chưa đủ',
      RESTORE_NOT_ALLOWED_BY_POLICY: 'Chính sách không cho phép khôi phục',
      PROMO_CODE_REJECTED: 'VNNIC từ chối mã khuyến mại',
      PROMO_CODE_REQUIRED: 'Đuôi tên miền này cần mã khuyến mại',
      PROMO_CODE_NOT_ELIGIBLE: 'Đuôi tên miền này không có chương trình miễn phí cho đơn hàng',
      REGISTRANT_TYPE_NOT_ALLOWED: 'Loại chủ thể này không được đăng ký đuôi tên miền',
      REGISTRANT_NATIONALITY_NOT_ALLOWED: 'Chủ thể có quốc tịch này không được đăng ký đuôi tên miền',
      MANUAL_ACTIVATION_REJECTED: 'Nhân viên đã từ chối kích hoạt tên miền thuộc đuôi cần duyệt thủ công',
      // An order paid by bank transfer whose payment request ended unpaid (crates/ordering/src/pay_later.rs)
      PAYMENT_NOT_RECEIVED: 'Không nhận được tiền thanh toán',
      // A sold service (crates/subscriptions): a delivery that definitely failed (or staff gave up), a renewal of a
      // service that has ended, a renewal the provider refused
      SERVICE_NOT_DELIVERED: 'Không cấp phát được dịch vụ',
      RENEWAL_NOT_APPLICABLE: 'Dịch vụ đã kết thúc, không gia hạn được',
      RENEWAL_REFUSED: 'Nhà cung cấp từ chối gia hạn dịch vụ',
      // ADR-027 G3 / G6 (registrar lifecycle): a .vn create the registry refused after review; a renewal the
      // registry made while the domain was no longer waiting for it (nothing captured, staff check)
      PENDING_CREATE_REFUSED: 'Registry từ chối đăng ký sau khi xét duyệt',
      RENEWAL_NOT_APPLIED: 'Registry đã gia hạn nhưng tên miền không ở trạng thái chờ gia hạn, cần nhân viên kiểm tra',
    },
    notice: {
      before_one: 'Trước hạn {{count}} ngày',
      before_other: 'Trước hạn {{count}} ngày',
      after_one: 'Sau hạn {{count}} ngày',
      after_other: 'Sau hạn {{count}} ngày',
      AUTO_RENEW_FAILED: 'Tự động gia hạn thất bại',
    },
    // Order (`ordering.orders`) and order-item (`ordering.order_items`) statuses, both.
    orderStatus: {
      PLACED: 'Đã đặt',
      PENDING: 'Chờ xử lý',
      FUNDS_RESERVED: 'Đã tạm giữ tiền',
      FULFILLING: 'Đang xử lý',
      PROCESSING: 'Đang xử lý',
      COMPLETED: 'Hoàn tất',
      PARTIALLY_COMPLETED: 'Hoàn tất một phần',
      FAILED: 'Thất bại',
      CANCELLED: 'Đã huỷ',
      FULFILLED: 'Đã thực hiện',
      REFUNDED: 'Đã hoàn tiền',
    },
    operation: {
      REGISTER: 'Đăng ký mới',
      RENEW: 'Gia hạn',
      TRANSFER: 'Chuyển nhà đăng ký',
      RESTORE: 'Khôi phục',
      REDEMPTION: 'Chuộc lại',
      CHANGE_REGISTRANT: 'Đổi chủ thể',
      CHANGE_NAMESERVERS: 'Đổi máy chủ tên',
      DELETE: 'Xoá tên miền',
      VNNIC_SYNC: 'Đồng bộ hồ sơ VNNIC',
    },
    hold: { ACTIVE: 'Đang tạm giữ', CAPTURED: 'Đã thu', RELEASED: 'Đã giải phóng', EXPIRED: 'Hết hạn' },
    refund: { PENDING: 'Chờ hoàn', COMPLETED: 'Đã hoàn', FAILED: 'Thất bại' },
    entry: {
      HOLD_CAPTURE: 'Thu tiền',
      REGISTRY_COST_ACCRUAL: 'Chi phí registry',
      REVERSAL: 'Bút toán đảo',
      WALLET_TOPUP: 'Nạp ví',
      REFUND: 'Hoàn tiền',
      WALLET_ADJUSTMENT: 'Điều chỉnh số dư',
      WALLET_PAYOUT: 'Hoàn tiền về nguồn',
    },
    invoice: { INVOICE: 'Hoá đơn', CREDIT_NOTE: 'Hoá đơn điều chỉnh giảm' },
    account: { ASSET: 'Tài sản', LIABILITY: 'Nợ phải trả', REVENUE: 'Doanh thu', EXPENSE: 'Chi phí', EQUITY: 'Vốn chủ sở hữu' },
    posting: { DEBIT: 'Nợ', CREDIT: 'Có' },
    transferState: { NONE: 'Không có yêu cầu chuyển', PENDING_IN: 'Đang chuyển đến', PENDING_OUT: 'Đang chuyển đi' },
    dnsState: { NONE: 'Chưa ghi nhận thay đổi DNS', DELEGATED: 'Đã trỏ máy chủ tên', SIGNED: 'Đã ký DNSSEC', UNSIGNED: 'Chưa ký DNSSEC' },
    trStatus: {
      pending: 'Đang chờ tại registry',
      clientApproved: 'Nhà đăng ký chuyển đã chấp thuận',
      clientCancelled: 'Nhà đăng ký nhận đã huỷ',
      clientRejected: 'Nhà đăng ký chuyển đã từ chối',
      serverApproved: 'Registry đã chấp thuận',
      serverCancelled: 'Registry đã huỷ',
    },
    errorClass: {
      TIMEOUT: 'Quá thời gian chờ phản hồi',
      TIMEOUT_BEFORE_SEND: 'Quá thời gian trước khi gửi',
      TIMEOUT_AFTER_SEND: 'Đã gửi nhưng quá thời gian chờ phản hồi',
      CONNECTION_RESET: 'Mất kết nối',
      CONNECTION_RESET_AFTER_SEND: 'Mất kết nối sau khi gửi',
      CLOSED_BEFORE_SEND: 'Kết nối bị đóng trước khi gửi',
      CONNECT_FAILED: 'Không kết nối được registry',
      TLS_FAILED: 'Lỗi kết nối bảo mật',
      IO_BEFORE_SEND: 'Lỗi mạng trước khi gửi',
      IO_AFTER_SEND: 'Lỗi mạng sau khi gửi',
      MALFORMED_RESPONSE: 'Phản hồi của registry không đọc được',
      FRAME_TOO_LARGE: 'Phản hồi quá lớn',
      LOGIN_REJECTED: 'Registry từ chối đăng nhập',
      TRID_MISMATCH: 'Phản hồi không khớp với lệnh đã gửi',
      SESSION_UNUSABLE: 'Phiên làm việc với registry không dùng được',
      WORKER_LOST_AFTER_SEND: 'Xử lý bị gián đoạn sau khi gửi',
      UNSUPPORTED_BY_REGISTRY: 'Registry không hỗ trợ',
      REGISTRY_TIMEOUT: 'Registry không phản hồi kịp',
    },
    trafficClass: { INTERACTIVE: 'Tương tác (ưu tiên)', BACKGROUND: 'Chạy nền', POLL: 'Nhận thông báo (poll)', DROPCATCH: 'Giành tên miền được giải phóng', STANDARD: 'Thông thường' },
    platformAccount: 'Nền tảng',
  },

  common: {
    id: 'ID',
    current: 'Hiện tại',
    ended: 'Đã kết thúc',
    since: 'từ {{when}}',
    until: 'đến {{when}}',
    none: 'Không có',
    unnamed: 'Đã ẩn tên',
    unnamedHint: 'Tên chủ thể chỉ hiển thị với nhân viên có quyền xem chủ thể.',
    noSubject: 'Không gắn chủ thể',
    yes: 'Có',
    no: 'Không',
    loadFailed: 'Không tải được mục này',
    open: 'Mở',
    version: 'v{{n}}',
  },

  list: {
    title: 'Tên miền',
    subtitle: 'Tìm tên miền theo tên và xem nhanh vòng đời, hồ sơ, tuân thủ và ngày hết hạn.',
    crumb: 'Kinh doanh',
    name: 'Tên bắt đầu bằng',
    namePlaceholder: 'vd: example hoặc ví-dụ',
    lifecycle: 'Vòng đời',
    any: 'Mọi trạng thái',
    search: 'Tìm',
    clear: 'Xoá bộ lọc',
    advanced: 'Bộ lọc nâng cao',
    activeCount: 'Đang bật {{count}} bộ lọc',
    nSelected: 'Đã chọn {{count}}',
    subjectSearch: 'Tên pháp lý của chủ thể',
    registry: 'Registry',
    registryPlaceholder: 'vd: VNNIC',
    tld: 'Đuôi tên miền',
    tldPlaceholder: 'vd: vn, com.vn, com',
    autoRenew: 'Tự gia hạn',
    yes: 'Bật',
    no: 'Tắt',
    expires: 'Hết hạn',
    basis: 'Tính theo',
    rangeFrom: '{{label}} từ',
    rangeTo: '{{label}} đến',
    withinDays: 'Hết hạn trong {{days}} ngày tới',
    expiresBefore: 'Hết hạn trước {{date}}',
    tableTitle: 'Danh sách tên miền',
    tableDescription: 'Khớp phần đầu của tên, dạng Unicode hoặc Punycode. Mặc định tên miền thêm gần nhất lên trước; bấm tiêu đề cột để sắp xếp. Bấm vào tên để mở tên miền.',
    colDomain: 'Tên miền',
    colCreated: 'Ngày thêm',
    colRegistered: 'Ngày đăng ký',
    colCustomer: 'Khách hàng',
    colSubject: 'Chủ thể',
    colLifecycle: 'Vòng đời',
    colDossier: 'Hồ sơ',
    colCompliance: 'Tuân thủ',
    colRegistryExpiry: 'Hết hạn tại registry',
    colCustomerExpiry: 'Hết hạn với khách hàng',
    differs: 'Lệch',
    differsHint: 'Ngày hết hạn với khách hàng khác ngày hết hạn tại registry',
    empty: 'Không có tên miền phù hợp',
    emptyFiltered: 'Không có tên miền nào khớp các bộ lọc đang bật. Nới hoặc xoá bộ lọc.',
    emptyAll: 'Chưa có tên miền nào.',
    emptyPage: 'Trang này nằm sau trang cuối của danh sách.',
    lastPage: 'Về trang cuối',
    pageInfo: 'Trang {{page}}/{{pages}} · {{total}} tên miền',
  },

  detail: {
    loading: 'Đang tải tên miền…',
    notFound: 'Không tìm thấy tên miền',
    loadFailed: 'Không tải được tên miền',
    refresh: 'Làm mới',
    sections: 'Các mục của tên miền',
    subtitle: 'Toàn bộ thông tin của một tên miền: trạng thái, chủ thể, registry, thanh toán, tuân thủ và lịch sử.',
  },

  upstream: {
    title: 'Xác minh tại nhà đăng ký cấp trên',
    hint: 'Tên miền này do nhà đăng ký cấp trên (WebNIC) quản lý: họ gửi e-mail xác minh chủ thể và tạm ngưng tên miền nếu chưa xác minh. Hệ thống không gửi e-mail xác minh riêng.',
    attention: 'Chủ thể chưa xác minh hoặc tên miền đang bị tạm ngưng tại nhà đăng ký cấp trên. Đã mở việc cần xử lý.',
    verified: 'Xác minh chủ thể',
    suspended: 'Tạm ngưng',
    privacy: 'Ẩn WHOIS (WebNIC)',
    checkedAt: 'Đọc lần cuối',
    resentAt: 'Gửi lại e-mail xác minh',
    lastError: 'Lỗi lần đọc gần nhất',
    yes: 'Đã xác minh',
    notVerified: 'Chưa xác minh',
    unknown: 'Không rõ',
    suspendedYes: 'Đang tạm ngưng',
    no: 'Không',
    notStated: 'Không có thông tin',
    on: 'Bật',
    off: 'Tắt',
    notRead: 'Chưa đọc trạng thái. Bấm "Đọc lại" để lấy từ nhà đăng ký cấp trên.',
    resend: 'Gửi lại email xác minh',
    resendTitle: 'Gửi lại e-mail xác minh của nhà đăng ký cấp trên',
    resendHint: 'WebNIC gửi lại đường dẫn xác minh tới e-mail chủ thể đang lưu tại WebNIC.',
    resent: 'Đã gửi tới {{to}}.',
    refresh: 'Đọc lại',
    refreshTitle: 'Đọc lại trạng thái xác minh / tạm ngưng',
    refreshed: 'Đã cập nhật.',
  },
  actions: {
    menu: 'Thao tác',
    description: 'Gửi ngay tới registry như một yêu cầu của khách hàng. Lý do được lưu vào nhật ký kiểm toán kèm tên bạn.',
    descriptions: {
      registryContacts: 'Đọc ngay tại registry, chỉ xem: không thay đổi gì ở registry hay trên hệ thống. Lần xem được ghi vào nhật ký kiểm toán.',
      email: 'Gửi theo mẫu email đang dùng của thương hiệu khách. Lý do được lưu vào nhật ký kiểm toán và lịch sử tên miền.',
    },
    items: {
      nameservers: 'Đổi name server',
      dnssec: 'DNSSEC (bản ghi DS)',
      lock: 'Khoá chuyển nhượng',
      transferCode: 'Cấp mã chuyển mới',
      updateLock: 'Khoá cập nhật',
      renew: 'Gia hạn',
      registrant: 'Đổi chủ thể',
      nameChange: 'Đổi tên / chuyển nhượng (.vn)',
      restore: 'Khôi phục tên miền',
      delete: 'Xoá tên miền',
      services: 'Dịch vụ tên miền (add-on)',
      hold: 'Khoá / mở khoá tên miền',
      registryContacts: 'Liên hệ tại registry',
      email: 'Gửi email',
    },
    related: {
      title: 'Liên quan',
      customer: 'Khách hàng',
      subject: 'Chủ thể đứng tên',
      dossier: 'Hồ sơ',
      orders: 'Đơn hàng',
      invoices: 'Hoá đơn',
      transfers: 'Chuyển tên miền',
      registryTx: 'Giao dịch registry',
      work: 'Việc cần xử lý',
      history: 'Lịch sử',
    },
    reason: 'Lý do',
    reasonHint: 'Vì sao thực hiện (lưu vào nhật ký kiểm toán).',
    close: 'Đóng',
    choose: '— Chọn —',
    loading: 'Đang tải…',
    applied: 'Xong: registry đã áp dụng thay đổi.',
    pending: 'Đã gửi: registry chưa xác nhận; kết quả sẽ hiện trên tên miền.',
    ns: {
      label: 'Name server (mỗi dòng một, từ 2 đến 13)',
      hint: '{{count}} name server',
      submit: 'Lưu name server',
      glueTitle: 'Địa chỉ IP gửi kèm máy chủ tên (glue)',
      glueHint:
        'Chỉ máy chủ tên nằm dưới chính tên miền này (ví dụ ns1.{{domain}}) mới gửi kèm địa chỉ IP và cần ít nhất một địa chỉ IPv4 hoặc IPv6; nhiều địa chỉ cách nhau bằng dấu phẩy. Máy chủ tên khác chỉ gửi tên.',
      ipv4: 'IPv4',
      ipv6: 'IPv6',
      ipv4Placeholder: '192.0.2.1',
      ipv6Placeholder: '2001:db8::1',
      external: 'Chỉ gửi tên, không kèm IP: {{list}}',
      notIpv4: 'Không phải địa chỉ IPv4: {{list}}',
      notIpv6: 'Không phải địa chỉ IPv6: {{list}}',
      glueMissing: 'Mỗi máy chủ tên thuộc tên miền này cần ít nhất một địa chỉ IP.',
    },
    ds: {
      none: 'Không có bản ghi DS: DNSSEC đang tắt.',
      unsupported: 'Registry này không báo hỗ trợ DNSSEC.',
      keyTag: 'Key tag',
      algorithm: 'Thuật toán',
      digestType: 'Kiểu digest',
      digest: 'Digest',
      publicKey: 'Khoá công khai (DNSKEY)',
      publicKeyHint: 'VNNIC bắt buộc với .vn; nơi khác không bắt buộc.',
      add: 'Thêm bản ghi DS',
      remove: 'Bỏ',
      submit: 'Lưu DNSSEC',
      turnOff: 'Tắt DNSSEC',
    },
    lock: {
      nowLocked: 'Tên miền đang khoá chuyển nhượng.',
      nowUnlocked: 'Tên miền đang không khoá chuyển nhượng.',
      lock: 'Khoá',
      unlock: 'Mở khoá',
      heldUntil: 'Khoá theo chính sách chuyển đổi (sau đăng ký, chuyển về hoặc đổi chủ thể) còn hiệu lực đến {{until}}: chưa mở khoá được trước thời điểm này.',
      held: 'Khoá theo chính sách chuyển đổi đang có hiệu lực: chưa mở khoá được.',
    },
    updateLock: {
      nowLocked: 'Tên miền đang khoá cập nhật (clientUpdateProhibited): registry từ chối mọi thay đổi chưa gỡ khoá.',
      nowUnlocked: 'Tên miền đang không khoá cập nhật.',
      hint: 'Hệ thống tự khoá sau mỗi lần đổi liên hệ và tự gỡ rồi khoá lại quanh các thay đổi của chính hệ thống (máy chủ tên, DNSSEC, khoá chuyển, gia hạn).',
      lock: 'Khoá cập nhật',
      unlock: 'Mở khoá cập nhật',
    },
    hold: {
      nowHeld: 'Tên miền đang bị khoá (clientHold): registry ngừng phân giải DNS của tên miền.',
      nowFree: 'Tên miền đang không bị khoá (không có clientHold).',
      hint: 'Khoá tên miền đặt clientHold tại registry: tên miền ngừng phân giải DNS cho tới khi mở khoá. Khoá do chưa xác minh e-mail (ICANN) hoặc do vụ việc lạm dụng được mở ở nơi đã đặt.',
      hold: 'Khoá tên miền',
      release: 'Mở khoá tên miền',
    },
    quick: {
      title: 'Thao tác nhanh',
      sync: 'Đồng bộ registry',
      syncBusy: 'Đang đọc tại registry…',
      synced: 'Đã đọc lại tại registry lúc {{time}}; trang đã cập nhật theo câu trả lời của registry.',
    },
    registryContacts: {
      hint: 'domain:info rồi contact:info từng liên hệ, đặt cạnh liên hệ trên hệ thống; số giấy tờ cá nhân được che, dòng khác nhau được tô màu.',
      readAt: 'Đọc lúc {{time}}.',
      reload: 'Đọc lại',
      field: 'Thông tin',
      atRegistry: 'Tại registry',
      inSystem: 'Trên hệ thống',
      sameHandle: 'Cùng contact ID với hệ thống',
      otherHandle: 'Hệ thống đang ghi contact ID khác: {{handle}}',
      notOurs: 'Contact do nhà đăng ký khác quản lý',
      reads: {
        NOT_FOUND: 'Registry không còn contact này.',
        UNSUPPORTED: 'Kết nối này không cho đọc liên hệ tại registry.',
        UNAVAILABLE: 'Không đọc được liên hệ lúc này ({{outcome}}).',
        NONE: 'Registry không có liên hệ cho vai trò này.',
      },
      fields: { name: 'Tên', organization: 'Tổ chức', email: 'E-mail', phone: 'Điện thoại', fax: 'Fax', address: 'Địa chỉ', identity: 'Số giấy tờ' },
    },
    email: {
      template: 'Mẫu email',
      templates: {
        verifyEmail: 'Gửi lại email xác minh (ICANN)',
        unverifiedSuspended: 'Thông báo tạm ngưng do chưa xác minh e-mail, kèm đường dẫn xác minh (ICANN)',
        dataReminder: 'Nhắc kiểm tra thông tin đăng ký (ICANN WDRP)',
        expiryReminder: 'Nhắc hạn tên miền (ICANN ERRP)',
        expiredNotice: 'Thông báo tên miền đã hết hạn (ICANN ERRP)',
      },
      unavailable: {
        ICANN_ONLY: 'chỉ cho tên miền quốc tế (ICANN)',
        ALREADY_VERIFIED: 'e-mail chủ thể đã được xác minh',
        NO_EMAIL: 'chủ thể chưa có e-mail',
        NOT_LIVE: 'tên miền không còn tại registry',
      },
      actual: 'Thư được gửi: {{template}}.',
      upstream: 'Nhà đăng ký cấp trên (WebNIC) tự gửi thư xác minh tới e-mail chủ thể đã đăng ký với họ; hệ thống yêu cầu họ gửi lại.',
      to: 'Người nhận',
      subject: 'Tiêu đề',
      preview: 'Xem trước email',
      linkNote: 'Đường dẫn xác minh dùng một lần được tạo khi gửi.',
      submit: 'Gửi email',
      queued: 'Đã đưa vào hàng gửi tới {{to}}; lịch sử tên miền ghi lại lần gửi này.',
      sent: 'Đã yêu cầu nhà đăng ký cấp trên gửi lại tới {{to}}.',
    },
    code: {
      warning: 'Mã chuyển hiện tại sẽ hết hiệu lực. Chỉ đưa mã mới cho chủ thể.',
      code: 'Mã chuyển mới',
      once: 'Chỉ hiện lúc này; hệ thống không lưu dạng đọc được.',
      sent: 'Registry đã gửi mã mới tới e-mail của chủ thể ({{recipient}}).',
      submit: 'Cấp mã mới',
    },
    restore: {
      price: 'Phí khôi phục {{total}} · trừ vào tài khoản: {{customer}}',
      started: 'Đã đặt khôi phục; kết quả từ registry sẽ hiện trên tên miền trong giây lát.',
      submit: 'Khôi phục',
    },
    renew: {
      years: 'Thời hạn',
      yearsN_one: '{{count}} năm',
      yearsN_other: '{{count}} năm',
      price: 'Tổng {{total}} · trừ vào tài khoản: {{customer}}',
      started: 'Đã đặt gia hạn; kết quả từ registry sẽ hiện trên tên miền trong giây lát.',
      submit: 'Gia hạn',
      recent: 'Gia hạn bây giờ sẽ cộng thêm {{count}} năm nữa vào hạn mới.',
      confirm: 'Tôi xác nhận gia hạn thêm dù tên miền vừa được gia hạn',
      confirmHint: 'Bắt buộc ghi lý do bên dưới; việc gia hạn vượt giới hạn 24 giờ được ghi vào nhật ký kiểm toán và lịch sử tên miền.',
      submitAnyway: 'Gia hạn thêm',
    },
    registrant: {
      nameChange: 'Chủ thể mới có tên khác: VNNIC chỉ đổi tên trên hệ thống quản lý của VNNIC, nên đã mở yêu cầu đổi tên (hồ sơ, duyệt).',
      openNameChange: 'Mở yêu cầu',
      to: 'Chủ thể mới',
      toHint: 'Một chủ thể của cùng khách hàng. Nếu chưa có, tạo ở trang Chủ thể trước.',
      optOut: 'Chủ thể từ chối khoá chuyển nhượng sau khi đổi (chỉ khi chính sách cho phép)',
      submit: 'Bắt đầu đổi',
      started: 'Đã bắt đầu. Ghi nhận cách mỗi bên đồng ý:',
      startedByEmail: 'Đã bắt đầu. Chủ thể hiện tại và chủ thể mới mỗi bên nhận một e-mail và đồng ý bằng đường dẫn trong đó; yêu cầu đổi được gửi tới registry khi cả hai đã đồng ý.',
      method: 'Hình thức xác nhận',
      methods: { IN_PERSON: 'Trực tiếp', SIGNED_DOCUMENT: 'Văn bản có chữ ký', DIGITAL_SIGNATURE: 'Chữ ký số', EMAIL_LINK: 'Xác nhận qua e-mail', PHONE_CALL: 'Gọi điện' },
      confirmCurrentRegistrant: 'Chủ thể hiện tại đã đồng ý',
      confirmNewRegistrant: 'Chủ thể mới đã đồng ý',
      authorized: 'Hai bên đã đồng ý: yêu cầu đổi được gửi tới registry.',
      contactTitle: 'Registrant mới tại registry',
      contactHint: 'Contact ID gửi registry cho chủ thể này. Việc đổi tự tạo contact khi tới registry; có thể tạo trước để có contact ID ngay, việc đổi sẽ dùng chính contact đó.',
      contactCreated: 'đã có tại registry',
      contactNotCreated: 'chưa tạo tại registry',
      createContact: 'Tạo registrant mới',
      contactReason: 'Ghi lý do ở trên trước.',
      handleSent: 'Contact registrant gửi registry: {{handle}}',
    },
    delete: {
      warning: 'Tên miền sẽ bị xoá tại registry. Một số registry cho khôi phục trong thời gian ngắn; VNNIC thì không.',
      type: 'Gõ {{name}} để xác nhận',
      submit: 'Xoá',
    },
  },

  tabs: {
    overview: 'Tổng quan',
    lifecycle: 'Vòng đời',
    subject: 'Chủ thể',
    dossier: 'Hồ sơ',
    documents: 'Tài liệu',
    registry: 'Registry',
    epp: 'EPP',
    contacts: 'Liên hệ',
    nameservers: 'Máy chủ tên',
    dnssec: 'DNSSEC',
    orders: 'Đơn hàng',
    billing: 'Thanh toán',
    invoices: 'Hoá đơn',
    transfers: 'Chuyển đổi nhà đăng ký',
    compliance: 'Tuân thủ',
    vnnic: 'VNNIC',
    history: 'Lịch sử',
    audit: 'Kiểm toán',
    observability: 'Giám sát',
    services: 'Dịch vụ đang dùng',
  },

  summary: {
    registrationStopped: 'Đăng ký bị dừng',
    label: 'Tóm tắt tên miền',
    domain: 'Tên miền',
    aLabel: 'A-label',
    lifecycle: 'Vòng đời',
    noStatuses: 'không có trạng thái registry',
    registry: 'Registry',
    expires: 'Hết hạn',
    registryExpiry: 'Registry',
    customerExpiry: 'Khách hàng',
    notSet: 'chưa đặt',
    behind: 'Khách hàng chậm hơn registry {{days}} ngày',
    ahead: 'Khách hàng sớm hơn registry {{days}} ngày',
    customer: 'Khách hàng',
    subject: 'Chủ thể',
    none: 'Không có',
    dossier: 'Hồ sơ',
    documents: 'v{{version}} · {{count}} tài liệu',
    compliance: 'Tuân thủ',
    locks: '{{count}} khoá đang áp dụng',
    service: 'Dịch vụ',
    autoRenew: 'Tự động gia hạn',
    autoRenewOn: 'Bật · {{years}} năm',
    off: 'Tắt',
    billing: 'Thanh toán',
  },

  stop: {
    incompleteTitle: 'Registry sẽ từ chối dữ liệu của chủ thể này. Bổ sung trước khi đặt hàng:',
    verificationTitle: 'Chủ thể chưa được xác minh: vẫn đặt hàng được, nhưng tên miền chờ ở bước "Chờ định danh" cho tới khi chủ thể được xác minh.',
    title: 'Đăng ký bị dừng',
    refused: 'Lệnh bị từ chối:',
    notSent: 'chưa gửi tới registry',
    vnnicReason: 'Lý do VNNIC không cấp mã khuyến mại:',
    todo: 'Cần làm',
    editSubject: 'Sửa chủ thể',
    editRepresentative: 'Sửa người đại diện',
    subjectChanged: 'Chủ thể đã có dữ liệu mới. Bấm Thử lại để dùng dữ liệu mới (hồ sơ được lập lại theo phiên bản mới).',
    held: 'Đang tạm giữ:',
    workItem: 'Mở việc cần xử lý',
    waitingTitle: 'Đăng ký đang chờ hồ sơ',
    reusable: 'Chủ thể đã có định danh hợp lệ ({{method}}, {{when}}). Thử lại sẽ gắn định danh này vào hồ sơ và kiểm tra lại ngay.',
    retryWaitingBody: 'Hồ sơ tên miền được kiểm tra lại ngay. Nếu chủ thể đã có định danh hợp lệ, định danh đó được gắn vào hồ sơ; nếu chủ thể đã được sửa, hồ sơ được lập lại theo dữ liệu mới.',
    retry: 'Thử lại',
    cancel: 'Huỷ đăng ký',
    retryTitle: 'Thử lại đăng ký',
    retryBody: 'Quy trình đăng ký chạy lại. Nếu chủ thể đã được sửa, hồ sơ được lập lại theo dữ liệu mới trước khi gửi registry.',
    cancelTitle: 'Huỷ đăng ký',
    cancelBody: 'Đơn hàng được huỷ và khoản tiền đang tạm giữ được hoàn lại cho khách. Không thể hoàn tác.',
    cancelBodyNothingHeld: 'Đơn hàng được huỷ. Không thể hoàn tác.',
    reason: 'Lý do',
    back: 'Quay lại',
    retried: 'Đã cho chạy lại quy trình đăng ký.',
    cancelled: 'Đã huỷ đăng ký; khoản tạm giữ sẽ được hoàn lại.',
    cancelledNothingHeld: 'Đã huỷ đăng ký.',
    failed: 'Không thực hiện được.',
    openOverview: 'Xem cách xử lý',
    failedTitle: 'Đăng ký không thành công',
    reactivate: 'Kích hoạt lại đơn',
    reactivateTitle: 'Kích hoạt lại đơn đăng ký',
    reactivateBody:
      'Đơn hàng và tên miền này được xử lý lại theo quy trình đăng ký (hồ sơ, kiểm tra, liên hệ, lệnh tạo tên miền); không tạo đơn mới. Ví của khách tạm giữ lại {{amount}} (đang có thể dùng {{available}}); tiền chỉ bị trừ khi tên miền được tạo, và được trả lại nếu đăng ký lại thất bại.',
    reactivateHolds: 'Kích hoạt lại sẽ tạm giữ:',
    walletAvailable: 'Ví của khách có thể dùng:',
    reactivateShort: 'Số dư ví của khách không đủ để tạm giữ lại tiền của đơn: khách cần nạp thêm trước khi kích hoạt lại.',
    reactivated: 'Đã kích hoạt lại đơn; quy trình đăng ký chạy lại từ bước hồ sơ.',
  },

  overview: {
    registrationFailed: 'Đăng ký thất bại: {{reason}}',
    attention: 'Cần chú ý',
    attentionDescription: 'Những điểm cần xem trước tiên, suy ra từ bản ghi tên miền.',
    action: 'Xử lý',
    check: 'Kiểm tra',
    nothing: 'Không có gì cần chú ý.',
    txFailed: 'Không tải được các lệnh registry nên chưa kiểm tra được kết quả chưa xác định.',
    unresolved: '{{count}} lệnh registry chưa có kết quả cuối cùng, đang chờ đối soát. Không gửi lại thủ công.',
    pendingOrder: 'Nhà đăng ký cấp trên đang xử lý {{count}} đơn (đơn chờ duyệt); hệ thống tự theo dõi đến khi có kết quả. Không gửi lại thủ công.',
    service: 'Dịch vụ: {{state}}.',
    compliance: 'Tuân thủ: {{state}}.',
    locks: '{{count}} khoá pháp lý/tuân thủ: {{list}}.',
    dossier: 'Hồ sơ: {{state}}.',
    overdue: 'Thanh toán quá hạn.',
    paidThrough: 'Khách hàng đã thanh toán đến {{customer}} nhưng registry hết hạn {{registry}}.',
    expiresNoRenew: 'Hết hạn {{when}} và đang tắt tự động gia hạn.',
    lifecycle: 'Vòng đời: {{state}}.',
    openRegistry: 'Mở Registry',
    openCompliance: 'Mở Tuân thủ',
    openLocks: 'Xem khoá',
    openDossier: 'Mở Hồ sơ',
    openBilling: 'Mở Thanh toán',
    openLifecycle: 'Mở Vòng đời',
    openHistory: 'Mở Lịch sử',
    transferPending: 'Đang có yêu cầu chuyển đổi nhà đăng ký.',
    openTransfers: 'Mở Chuyển đổi nhà đăng ký',
    record: 'Các bên liên quan',
    domainId: 'ID tên miền',
    customer: 'Khách hàng',
    subject: 'Chủ thể',
    registry: 'Registry',
    tld: 'TLD',
    created: 'Tạo lúc',
    updated: 'Cập nhật',
    trace: 'Truy vết',
    traceBody: 'Đi từ sự kiện của tên miền đến workflow, lệnh registry, trace và log mà không cần sao chép ID.',
    historyChain: 'Lịch sử (chuỗi băm)',
    registryTxs: 'Lệnh registry',
    observabilityLinks: 'Liên kết giám sát',
  },

  lifecycle: {
    state: 'Trạng thái',
    stateDescription: 'Vị trí của tên miền trên vòng đời chuẩn.',
    pathLabel: 'Vòng đời',
    offPath: 'Nằm ngoài vòng đời chuẩn:',
    changed: 'Đổi vòng đời lúc',
    transferState: 'Trạng thái chuyển đổi nhà đăng ký',
    dnsState: 'Trạng thái DNS',
    recordVersion: 'Phiên bản bản ghi',
    keyDates: 'Các mốc thời gian',
    createdRegistrar: 'Tạo (nhà đăng ký)',
    createdRegistry: 'Tạo (registry)',
    registryExpiry: 'Hết hạn tại registry',
    paidThrough: 'Khách hàng đã thanh toán đến',
    autoRenew: 'Tự động gia hạn',
    autoRenewOn: 'Bật, {{years}} năm',
    off: 'Tắt',
    authInfo: 'Đặt AuthInfo lúc',
    note: 'Ngày hết hạn tại registry là dữ liệu registry đang lưu. Ngày hết hạn với khách hàng là thời hạn khách đã thanh toán. Hai ngày lệch nhau khi registry tự gia hạn nhưng chưa tính phí, hoặc khi khách đã trả tiền gia hạn nhưng registry chưa xác nhận.',
  },

  subjectTab: {
    none: 'Tên miền chưa gắn chủ thể',
    noneBody: 'Chủ thể (chủ sở hữu) được gắn khi tạo hồ sơ. Kiểm tra thẻ Hồ sơ.',
  },

  dossier: {
    origin: 'Nguồn',
    copiedFrom: 'Sao chép từ hồ sơ đã xác minh của chủ thể ngày {{date}}',
    copiedFromVerification: '(bằng chứng xác minh)',
    copiedFromSubject: '(tài liệu của chủ thể)',
    ownDocument: 'Tài liệu riêng của tên miền',
    verificationCopy: 'Kết quả xác minh trong hồ sơ',
    verificationCopyBody: 'Bản sao kết quả xác minh danh tính của chủ thể tại thời điểm hồ sơ này nhận; thay đổi về sau của chủ thể không làm thay đổi hồ sơ.',
    method: 'Phương thức',
    result: 'Kết quả',
    verifiedAt: 'Xác minh lúc',
    fromSubject: 'Chủ thể',
    openSubjectVerification: 'Xem xác minh và tài liệu của chủ thể',
    copiedAt: 'Sao chép lúc',
    vnnicFiles: 'Tệp gửi VNNIC',
    vnnicFilesBody: 'Hồ sơ "{{docType}}", hình thức xác thực "{{method}}": các tệp VNNIC HSTM-API v1.3 Phụ lục 2 yêu cầu, với tình trạng ký số từng tệp. Hồ sơ chỉ "Hoàn tất" khi có đủ đúng các tệp này.',
    fileTypeNone: 'Không thuộc loại tệp nào của VNNIC',
    fileTypes: { 1: 'CMND/CCCD mặt trước', 2: 'CMND/CCCD mặt sau', 3: 'Hộ chiếu', 4: 'Giấy phép ĐKKD', 5: 'Bản khai đăng ký tên miền', 6: 'Biên bản giải trình và cam kết khi đăng ký tên miền dưới gov.vn', 7: 'Cam kết đăng ký và sử dụng tên miền chứa từ khóa nhạy cảm', 8: 'Đơn đề nghị thay đổi tên chủ thể tên miền', 9: 'Quyết định đổi tên', 10: 'Bản cam kết nghĩa vụ nộp thuế chuyển nhượng tên miền', 11: 'Chứng từ nộp thuế chuyển nhượng tên miền', 12: 'Văn bản chuyển nhượng tên miền', 13: 'Đơn đề nghị hoàn trả tên miền', 14: 'Đơn đề nghị thay đổi thông tin', 15: 'Bản khai chuyển đổi NĐK', 16: 'Thông tin đăng ký tên miền của tổ chức được xác thực bằng chữ ký số' },
    signedStatus: { 0: 'chưa ký số', 1: 'ký số bởi chủ thể', 2: 'ký số bởi nhà đăng ký' },
    hstmSent: 'gửi',
    hstmRejected: 'Không dùng được (quy định tệp VNNIC)',
    hstmErrors: 'Chưa gửi được VNNIC',
    hstmComplete: 'Đủ tệp theo Phụ lục 2',
    certifyMethods: { 1: 'Xác thực tài khoản bằng EKYC', 2: 'Xác thực tài khoản bằng chữ ký số', 3: 'Xác thực tên miền bằng EKYC', 4: 'Xác thực tên miền bằng chữ ký số', 5: 'Xác thực bằng nhân công' },
    required: 'Bắt buộc',
    notUploaded: 'giữ trong hồ sơ, không gửi',
    missing: 'Thiếu',
    none: 'Chưa có hồ sơ',
    noneBody: 'Mọi tên miền do nhà đăng ký quản lý đều cần hồ sơ. Trạng thái hồ sơ hiện tại của tên miền là',
    title: 'Hồ sơ',
    status: 'Trạng thái',
    type: 'Loại',
    id: 'ID hồ sơ',
    version: 'Phiên bản',
    subject: 'Chủ thể',
    subjectVersion: 'Phiên bản chủ thể',
    verification: 'Xác minh danh tính',
    policy: 'Chính sách',
    completed: 'Hoàn tất lúc',
    openDossier: 'Mở trang hồ sơ',
    documents: 'Tài liệu gắn với hồ sơ',
    document: 'Tài liệu',
    purpose: 'Mục đích',
    bound: 'Gắn lúc',
    noDocuments: 'Chưa gắn tài liệu nào',
    noDocumentsBody: 'Tài liệu được đính kèm từ cổng khách hàng hoặc trang Tài liệu.',
  },

  documents: {
    title: 'Tài liệu của mọi phiên bản hồ sơ',
    description: 'Tài liệu gắn với các phiên bản hồ sơ của tên miền. Nội dung tệp chỉ mở được ở trang tài liệu và mọi lần xem đều được ghi lại.',
    loadFailed: 'Không tải được tài liệu',
    empty: 'Chưa có tài liệu',
    emptyBody: 'Hồ sơ của tên miền này chưa được đính kèm tài liệu nào.',
    document: 'Tài liệu',
    purpose: 'Mục đích',
    dossier: 'Hồ sơ',
    dossierVersion: 'Phiên bản {{n}}',
    file: 'Tệp',
    version: 'Phiên bản',
    notCurrent: 'đã thay thế',
    verification: 'Kiểm tra',
    uploaded: 'Tải lên',
    expires: 'hết hạn {{date}}',
  },

  registry: {
    lastRefusal: 'Lệnh gần nhất',
    noRefusal: 'Chưa gửi lệnh nào',
    object: 'Đối tượng tại registry',
    registry: 'Registry',
    tld: 'TLD',
    statuses: 'Trạng thái registry',
    statusSource: '{{source}}, ghi nhận {{at}}',
    none: 'Không có',
    expiry: 'Hết hạn tại registry',
    unresolved: 'Kết quả chưa xác định',
    unresolvedCount: '{{count}} lệnh chờ đối soát. Không gửi lại thủ công.',
    locks: 'Khoá đang áp dụng',
    lock: 'Khoá',
    reason: 'Lý do',
    case: 'Vụ việc',
    applied: 'Áp dụng lúc',
    autoRelease: 'Tự gỡ lúc',
    txs: 'Lệnh registry của tên miền',
    txsDescription: 'Mới nhất trước. Mở một lệnh để xem định danh, kết quả và các liên kết theo dấu.',
    txsFailed: 'Không tải được các lệnh registry',
    noTxs: 'Chưa có lệnh registry',
    noTxsBody: 'Các lệnh EPP gửi cho tên miền này sẽ hiện ở đây.',
    started: 'Bắt đầu',
    operation: 'Lệnh',
    state: 'Trạng thái',
    trids: 'clTRID / svTRID',
    noSvtrid: 'chưa có svTRID',
    result: 'Kết quả',
    latency: 'Độ trễ',
    workflow: 'Workflow',
    attempt: 'lần thử {{n}}',
    correlation: 'Correlation',
  },

  epp: {
    title: 'Lệnh gửi registry',
    description: 'Mọi lệnh gửi tới registry cho tên miền này và cho các liên hệ, máy chủ tên mà tên miền dùng, kèm kết quả và thông báo của registry. Mở một lệnh để xem chi tiết.',
    loadFailed: 'Không tải được các lệnh EPP',
    operation: 'Lệnh',
    anyOperation: 'Mọi lệnh',
    state: 'Trạng thái',
    anyState: 'Mọi trạng thái',
    object: 'Đối tượng',
    anyObject: 'Mọi đối tượng',
    trid: 'clTRID hoặc svTRID',
    tridPlaceholder: 'Mã giao dịch chính xác',
    search: 'Lọc',
    empty: 'Không có lệnh EPP',
    emptyFiltered: 'Không có lệnh nào khớp bộ lọc.',
    emptyAll: 'Chưa có lệnh nào được gửi cho tên miền này.',
    started: 'Bắt đầu',
    command: 'Lệnh',
    result: 'Kết quả',
    trids: 'clTRID / svTRID',
    latency: 'Độ trễ',
    workflow: 'Workflow',
    shown: 'Hiển thị {{count}}',
  },

  contacts: {
    data: {
      title: 'Thông tin liên hệ',
      description: 'Dữ liệu registry nhận cho liên hệ này, lấy từ phiên bản chủ thể dùng để tạo. Số giấy tờ được che; mở chủ thể để xem đầy đủ (có ghi nhật ký).',
      hidden: 'Bạn không có quyền xem dữ liệu chủ thể.',
      name: 'Tên',
      organization: 'Tổ chức',
      tradeName: 'Tên giao dịch',
      kind: 'Loại',
      email: 'E-mail',
      phone: 'Điện thoại',
      fax: 'Fax',
      gender: 'Giới tính',
      genders: { Male: 'Nam', Female: 'Nữ' },
      position: 'Chức vụ',
      nationality: 'Quốc tịch',
      birthDate: 'Ngày sinh',
      onFile: 'Đã có',
      identity: 'Giấy tờ định danh',
      address: 'Địa chỉ',
      province: 'Tỉnh / thành phố',
      ward: 'Phường / xã',
    },
    tabTitle: 'Liên hệ tại registry',
    tabDescription: 'Bốn liên hệ của riêng tên miền này (chủ thể, quản trị, kỹ thuật, thanh toán), sao chép từ chủ thể. Sửa ở đây chỉ thay đổi tên miền này.',
    thin: 'Registry này không lưu đối tượng liên hệ; các liên hệ dưới đây chỉ được lưu tại nhà đăng ký.',
    loadFailed: 'Không tải được danh sách liên hệ',
    empty: 'Chưa có liên hệ',
    emptyBody: 'Liên hệ được gán khi đăng ký tên miền.',
    role: 'Vai trò',
    contact: 'Liên hệ',
    noHandle: 'Chỉ lưu tại nhà đăng ký',
    subject: 'Thuộc về',
    period: 'Thời gian gán',
    usage: 'Số tên miền dùng',
    usedBy_one: '{{count}} tên miền',
    usedBy_other: '{{count}} tên miền',
    registrarContact: 'Liên hệ của chính nhà đăng ký',
    provenance: 'Nguồn',
    origins: { CLONED: 'Sao chép từ chủ thể', SYNCED: 'Đồng bộ từ chủ thể', EDITED: 'Sửa riêng cho tên miền' },
    clonedAt: 'Sao chép lúc {{date}}',
    updatedAt: 'Cập nhật lúc {{date}}',
    differs: 'Khác chủ thể hiện tại: {{fields}}',
    matches: 'Khớp chủ thể hiện tại',
    fields: { legal_name: 'tên', identifier: 'số giấy tờ', address: 'địa chỉ', email: 'e-mail', phone: 'điện thoại', fax: 'fax', details: 'thông tin khác' },
    edit: 'Sửa',
    editTitle: 'Sửa liên hệ: {{role}}',
    editDescription: 'Chỉ liên hệ này của {{domain}} thay đổi; các tên miền khác giữ nguyên.',
    editSubject: 'Lấy thông tin từ chủ thể',
    editSubjectHint: 'Liên hệ nhận thông tin hiện tại của chủ thể được chọn.',
    editSubmit: 'Cập nhật liên hệ',
    editStarted: 'Đang cập nhật liên hệ tại registry. Tải lại mục này để xem kết quả.',
    registrantHint: 'Chủ thể được đổi qua Thao tác › Đổi chủ thể.',

    title: 'Liên hệ',
    subtitle: 'Các đối tượng liên hệ tại registry của những tên miền chúng ta quản lý, và chủ sở hữu của từng liên hệ.',
    crumb: 'Kinh doanh',
    filterQuery: 'Mã liên hệ hoặc tên',
    filterPlaceholder: 'vd: TINO-C hoặc Nguyễn',
    filterRole: 'Vai trò hiện tại',
    anyRole: 'Mọi vai trò',
    filterRegistry: 'Registry',
    anyRegistry: 'Mọi registry',
    search: 'Tìm',
    tableTitle: 'Đối tượng liên hệ',
    tableDescription: 'Sắp xếp theo registry và mã liên hệ. Thông tin cá nhân luôn được che; mở một liên hệ để xem các tên miền đang dùng.',
    colContact: 'Liên hệ',
    colRegistry: 'Registry',
    colSubject: 'Thuộc về',
    colRoles: 'Vai trò hiện tại',
    colDomains: 'Tên miền',
    colAssigned: 'Gán gần nhất',
    noRoles: 'Không còn dùng',
    totalDomains: 'tổng cộng {{count}}',
    emptyList: 'Không có liên hệ phù hợp',
    emptyListBody: 'Thử mã liên hệ ngắn hơn, tên khác hoặc đặt lại bộ lọc.',
    shown: 'Hiển thị {{count}} trên trang này',

    detailTitle: 'Liên hệ {{handle}}',
    detailSubtitle: 'Một đối tượng liên hệ tại {{registry}}: chủ sở hữu, các tên miền đang dùng và các lệnh đã gửi cho liên hệ này.',
    notFound: 'Không tìm thấy liên hệ',
    loadDetailFailed: 'Không tải được liên hệ',
    object: 'Đối tượng liên hệ',
    handle: 'Mã liên hệ',
    registry: 'Registry',
    roles: 'Vai trò hiện tại',
    domainCount: 'Số tên miền hiện dùng',
    firstUsed: 'Dùng lần đầu',
    lastAssigned: 'Gán gần nhất',
    behind: 'Thuộc về',
    behindDescription: 'Phiên bản chủ thể dùng để tạo liên hệ. Số định danh và thông tin liên lạc vẫn được che trên trang chủ thể.',
    subjectName: 'Tên',
    subjectType: 'Loại',
    subjectVersion: 'Phiên bản',
    notCurrentVersion: 'phiên bản cũ',
    verification: 'Xác minh',
    country: 'Quốc gia',
    managingCustomer: 'Khách hàng quản lý',
    noSubjectBody: 'Liên hệ này không gắn với chủ thể nào; đây là liên hệ của chính nhà đăng ký.',
    domains: 'Tên miền dùng liên hệ này',
    domain: 'Tên miền',
    customer: 'Khách hàng',
    lifecycle: 'Vòng đời',
    expiry: 'Hết hạn tại registry',
    commands: 'Lệnh registry của liên hệ này',
    commandsHidden: 'Bạn không có quyền xem lệnh registry.',
    noCommands: 'Chưa có lệnh registry nào cho liên hệ này',
  },

  delegation: {
    loadFailed: 'Không tải được thông tin uỷ quyền',
    nsTitle: 'Máy chủ tên',
    nsDescription: 'Nơi tên miền được uỷ quyền. Địa chỉ glue chỉ lưu cho các máy chủ tên nằm dưới chính tên miền này.',
    dnsState: 'Trạng thái DNS',
    host: 'Máy chủ tên',
    glue: 'Địa chỉ glue',
    noGlue: 'không cần',
    inBailiwick: 'Nằm dưới tên miền này',
    external: 'Bên ngoài',
    updated: 'Cập nhật',
    noNs: 'Chưa có máy chủ tên',
    noNsBody: 'Tên miền chưa được uỷ quyền. Máy chủ tên được đặt từ cổng khách hàng hoặc bởi bộ phận hỗ trợ.',
    dsTitle: 'Bản ghi DS',
    dsDescription: 'Các bản ghi DS (delegation signer) của tên miền đang công bố tại registry.',
    signed: 'Đã ký DNSSEC',
    unsigned: 'Chưa ký',
    notSupported: 'Registry không nhận DNSSEC cho TLD này.',
    keyTag: 'Key tag',
    algorithm: 'Thuật toán',
    digestType: 'Loại digest',
    digest: 'Digest',
    noDs: 'Chưa có bản ghi DS',
    noDsBody: 'Tên miền chưa bật DNSSEC.',
  },

  orders: {
    title: 'Đơn hàng',
    description: 'Các mục đơn hàng đã đăng ký, gia hạn hoặc chuyển nhà đăng ký cho tên miền này.',
    loadFailed: 'Không tải được đơn hàng',
    empty: 'Chưa có đơn hàng',
    emptyBody: 'Chưa có mục đơn hàng nào cho tên miền này được đặt qua nền tảng.',
    order: 'Đơn hàng',
    operation: 'Loại',
    years_one: '{{count}} năm',
    years_other: '{{count}} năm',
    amount: 'Số tiền',
    status: 'Trạng thái',
    hold: 'Tiền tạm giữ',
    invoice: 'Hoá đơn',
    customer: 'Khách hàng',
    noInvoice: 'chưa xuất hoá đơn',
  },

  billing: {
    title: 'Thanh toán',
    description: 'Khách hàng trả tiền gì cho tên miền này, và mọi dòng tiền liên quan.',
    loadFailed: 'Không tải được thông tin thanh toán',
    state: 'Trạng thái thanh toán',
    customer: 'Tính phí cho',
    currency: 'Tiền tệ thanh toán',
    paidThrough: 'Đã thanh toán đến',
    registryExpiry: 'Hết hạn tại registry',
    autoRenew: 'Tự động gia hạn',
    autoRenewOn: 'Bật, {{years}} năm',
    off: 'Tắt',
    holds: 'Tiền tạm giữ',
    holdsDescription: 'Số tiền tạm giữ trong ví của khách hàng trong lúc thực hiện mục đơn hàng.',
    noHolds: 'Không có khoản tạm giữ',
    purpose: 'Mục đích',
    amount: 'Số tiền',
    status: 'Trạng thái',
    created: 'Tạo lúc',
    closed: 'Đóng lúc',
    expires: 'Hết hạn',
    refunds: 'Hoàn tiền',
    noRefunds: 'Không có khoản hoàn tiền',
    reason: 'Lý do',
    journal: 'Bút toán sổ cái',
    journalDescription: 'Các bút toán kép liên quan đến tên miền, mới nhất trước.',
    noJournal: 'Chưa có bút toán',
    entry: 'Bút toán',
    posted: 'Ghi sổ lúc',
    account: 'Tài khoản',
    debit: 'Nợ',
    credit: 'Có',
    reverses: 'đảo một bút toán trước đó',
    openEntry: 'Mở bút toán',
  },

  invoices: {
    title: 'Hoá đơn',
    description: 'Hoá đơn và hoá đơn điều chỉnh có tính phí cho tên miền này. Chỉ hiển thị các dòng của tên miền này.',
    loadFailed: 'Không tải được hoá đơn',
    empty: 'Chưa có hoá đơn',
    emptyBody: 'Chưa có khoản nào của tên miền này được xuất hoá đơn.',
    number: 'Số',
    type: 'Loại',
    credits: 'điều chỉnh cho {{label}}',
    customer: 'Khách hàng',
    issued: 'Ngày xuất',
    total: 'Tổng hoá đơn',
    lines: 'Các dòng của tên miền này',
    line: 'Dòng',
    quantity: 'SL',
    unitPrice: 'Đơn giá',
    tax: 'Thuế',
    amount: 'Thành tiền',
  },

  transfersTab: {
    state: 'Tình trạng chuyển đổi nhà đăng ký',
    stateDescription: 'Tên miền hiện có thể chuyển sang nhà đăng ký khác hay không.',
    transferState: 'Trạng thái chuyển đổi nhà đăng ký',
    authInfo: 'Đặt AuthInfo gần nhất',
    noAuthInfo: 'chưa từng đặt',
    activeLocks: 'Khoá chuyển nhà đăng ký đang áp dụng',
    noActiveLocks: 'Không có',
    transfers: 'Chuyển đổi nhà đăng ký',
    noTransfers: 'Chưa có lần chuyển nào',
    noTransfersBody: 'Tên miền chưa từng được chuyển đến hoặc chuyển đi.',
    changes: 'Đổi chủ thể',
    noChanges: 'Chưa có yêu cầu đổi chủ thể',
    locks: 'Khoá chuyển nhà đăng ký và khoá của nhà đăng ký',
    locksDescription: 'Bao gồm cả các khoá đã gỡ.',
    noLocks: 'Không có khoá chuyển nhà đăng ký',
    lock: 'Khoá',
    reason: 'Lý do',
    appliedBy: 'Áp dụng bởi',
    applied: 'Áp dụng lúc',
    released: 'Đã gỡ',
    active: 'Đang áp dụng',
    autoRelease: 'tự gỡ {{when}}',
    notices: 'Thông báo hết hạn đã gửi',
    noNotices: 'Chưa gửi thông báo hết hạn nào',
    notice: 'Thông báo',
    expiry: 'Về lần hết hạn',
    sent: 'Gửi lúc',
  },

  transfers: {
    title: 'Chuyển đổi nhà đăng ký',
    subtitle: 'Các tên miền đang chuyển đến hoặc chuyển đi nhà đăng ký khác và tiến độ của từng yêu cầu.',
    crumb: 'Kinh doanh',
    filterDomain: 'Tên miền bắt đầu bằng',
    filterDomainPlaceholder: 'vd: example',
    filterDirection: 'Chiều',
    anyDirection: 'Cả hai chiều',
    filterStatus: 'Trạng thái',
    anyStatus: 'Mọi trạng thái',
    search: 'Tìm',
    tableTitle: 'Yêu cầu chuyển đổi nhà đăng ký',
    tableDescription: 'Mới nhất trước. Bấm vào tên miền để mở yêu cầu: xác nhận, lệnh registry và lịch sử.',
    colDomain: 'Tên miền',
    colDirection: 'Chiều',
    colStatus: 'Trạng thái',
    colOther: 'Nhà đăng ký bên kia',
    colCustomer: 'Khách hàng',
    colRequested: 'Yêu cầu lúc',
    colNext: 'Hạn / quyết định',
    autoApprove: 'tự chấp thuận {{when}}',
    decided: 'quyết định {{when}}',
    completed: 'hoàn tất {{when}}',
    empty: 'Không có yêu cầu phù hợp',
    emptyBody: 'Thử trạng thái hoặc chiều khác, hoặc đặt lại bộ lọc.',
    shown: 'Hiển thị {{count}} trên trang này',

    detailTitle: '{{direction}}: {{domain}}',
    detailSubtitle: 'Một yêu cầu chuyển đổi nhà đăng ký của {{domain}}: trạng thái, xác nhận, lệnh registry và lịch sử liên quan.',
    notFound: 'Không tìm thấy yêu cầu chuyển đổi nhà đăng ký',
    loadFailed: 'Không tải được yêu cầu chuyển đổi nhà đăng ký',
    overview: 'Yêu cầu',
    domain: 'Tên miền',
    direction: 'Chiều',
    status: 'Trạng thái',
    registryStatus: 'Trạng thái tại registry',
    cancel: 'Huỷ chuyển đổi',
    cancelTitle: 'Huỷ yêu cầu chuyển đổi này',
    cancelDescription: 'Rút yêu cầu của chúng ta tại registry. Đơn hàng được giải phóng khi registry xác nhận.',
    cancelSubmit: 'Huỷ chuyển đổi',
    cancelled: 'Đã huỷ tại registry: đơn hàng sẽ được giải phóng.',
    cancelPending: 'Đã gửi: registry chưa xác nhận việc huỷ.',
    approveOut: 'Đồng ý cho chuyển đi',
    approveOutTitle: 'Đồng ý cho tên miền chuyển sang nhà đăng ký khác',
    approveOutDescription: 'Tên miền sẽ rời khỏi hệ thống khi registry hoàn tất việc chuyển. Chỉ đồng ý khi chủ thể đã xác nhận muốn chuyển đi.',
    approveOutSubmit: 'Đồng ý chuyển đi',
    approvedOut: 'Đã đồng ý tại registry: tên miền sẽ được chuyển đi.',
    rejectOut: 'Từ chối chuyển đi',
    rejectOutTitle: 'Từ chối yêu cầu chuyển tên miền đi',
    rejectOutDescription: 'Chỉ được từ chối vì một trong các lý do mà quy định chuyển đổi cho phép. Lý do được gửi tới registry.',
    rejectOutSubmit: 'Từ chối chuyển đi',
    rejectedOut: 'Đã từ chối tại registry: tên miền ở lại hệ thống.',
    respondPending: 'Đã gửi: registry chưa xác nhận câu trả lời.',
    respondAwaiting: 'Tên miền đang được bảo vệ: yêu cầu đã được ghi nhận và chờ nhân viên có thẩm quyền xác nhận.',
    ground: 'Lý do từ chối',
    groundPlaceholder: 'Chọn lý do',
    grounds: {
      EVIDENCE_OF_FRAUD: 'Có dấu hiệu gian lận',
      UDRP_OR_URS_ACTION: 'Đang có tranh chấp tên miền (UDRP / URS)',
      COURT_ORDER: 'Có quyết định của toà án',
      IDENTITY_DISPUTE: 'Tranh chấp về danh tính người yêu cầu',
      PAYMENT_DISPUTE: 'Tranh chấp thanh toán',
      EXPRESS_REGISTRANT_OBJECTION: 'Chủ thể phản đối việc chuyển đi',
      WITHIN_POST_REGISTRATION_OR_TRANSFER_LOCK: 'Trong thời gian khoá sau đăng ký hoặc sau chuyển đổi',
    },
    other: 'Nhà đăng ký bên kia',
    customer: 'Khách hàng',
    registry: 'Registry',
    lifecycle: 'Vòng đời tên miền',
    requested: 'Yêu cầu lúc',
    requestedBy: 'Yêu cầu bởi',
    registryPending: 'Chờ registry từ',
    autoApproveAt: 'Registry tự chấp thuận lúc',
    decidedAt: 'Quyết định lúc',
    decidedBy: 'Quyết định bởi',
    completedAt: 'Hoàn tất lúc',
    rejectReason: 'Lý do từ chối',
    cancelledByUs: 'Đã huỷ yêu cầu chuyển về: {{reason}}',
    authInfo: 'Có lưu AuthInfo',
    authInfoYes: 'Có (được mã hoá, không bao giờ hiển thị)',
    authInfoNo: 'Không',
    follow: 'Workflow và lệnh registry',
    workflow: 'Workflow',
    step: 'bước hiện tại: {{step}}',
    command: 'Lệnh registry',
    order: 'Mục đơn hàng',
    noOrder: 'Không có mục đơn hàng; lần chuyển này không tính phí qua nền tảng.',
    evidence: 'Xác nhận',
    evidenceDescription: 'Yêu cầu được xác nhận như thế nào. Không lưu số định danh cá nhân ở đây.',
    noEvidence: 'Chưa ghi nhận xác nhận nào',
    events: 'Lịch sử liên quan',
    noEvents: 'Không có sự kiện tên miền nào cho yêu cầu này',
    openHistory: 'Mở trong lịch sử tên miền',
  },

  changes: {
    title: 'Đổi chủ thể',
    subtitle: 'Các yêu cầu chuyển tên miền sang chủ thể mới, kèm xác nhận của hai bên và kết quả xác minh chủ thể mới.',
    crumb: 'Kinh doanh',
    filterDomain: 'Tên miền bắt đầu bằng',
    filterDomainPlaceholder: 'vd: example',
    filterStatus: 'Trạng thái',
    anyStatus: 'Mọi trạng thái',
    search: 'Tìm',
    tableTitle: 'Yêu cầu đổi chủ thể',
    tableDescription: 'Mới nhất trước. Bấm vào tên miền để mở yêu cầu: hai chủ thể, xác nhận của từng bên và lệnh registry.',
    colDomain: 'Tên miền',
    colFrom: 'Chủ thể hiện tại',
    colTo: 'Chủ thể mới',
    colStatus: 'Trạng thái',
    colCustomer: 'Khách hàng',
    colRequested: 'Yêu cầu lúc',
    lockOptOut: 'miễn khoá 60 ngày',
    empty: 'Không có yêu cầu phù hợp',
    emptyBody: 'Thử trạng thái khác hoặc đặt lại bộ lọc.',
    shown: 'Hiển thị {{count}} trên trang này',

    detailTitle: 'Đổi chủ thể: {{domain}}',
    detailSubtitle: 'Chuyển {{domain}} sang chủ thể mới: xác nhận, xác minh và lệnh registry.',
    notFound: 'Không tìm thấy yêu cầu đổi chủ thể',
    loadFailed: 'Không tải được yêu cầu đổi chủ thể',
    overview: 'Yêu cầu',
    domain: 'Tên miền',
    status: 'Trạng thái',
    customer: 'Khách hàng',
    registry: 'Registry',
    requested: 'Yêu cầu lúc',
    requestedBy: 'Yêu cầu bởi',
    completedAt: 'Kết thúc lúc',
    failure: 'Kết quả',
    optOut: 'Khoá chuyển nhà đăng ký 60 ngày',
    optOutYes: 'Chủ thể mới đã chọn miễn',
    optOutNo: 'Áp dụng sau khi đổi',
    from: 'Chủ thể hiện tại',
    to: 'Chủ thể mới',
    version: 'Phiên bản',
    country: 'Quốc gia',
    verification: 'Xác minh',
    verifiedAt: 'Xác minh lúc',
    verificationExpires: 'Hiệu lực đến',
    method: 'Phương thức',
    newDossier: 'Hồ sơ mới',
    noNewDossier: 'Chưa tạo',
    follow: 'Workflow và lệnh registry',
    workflow: 'Workflow',
    step: 'bước hiện tại: {{step}}',
    command: 'Lệnh registry',
    noCommand: 'Chưa có lệnh registry',
    evidence: 'Xác nhận',
    evidenceDescription: 'Ai đã xác nhận việc đổi chủ thể, bằng cách nào và khi nào. Không lưu số định danh cá nhân ở đây.',
    events: 'Lịch sử liên quan',
    noEvents: 'Không có sự kiện tên miền nào cho yêu cầu này',
  },

  evidence: {
    method: 'Phương thức',
    confirmedAt: 'Xác nhận lúc',
    notConfirmed: 'Chưa xác nhận',
    actorType: 'Người xác nhận',
    agreementVersion: 'Phiên bản thoả thuận',
    channel: 'Kênh',
    side: { current_registrant: 'Chủ thể hiện tại', new_registrant: 'Chủ thể mới', gaining_registrar: 'Nhà đăng ký nhận', losing_registrar: 'Nhà đăng ký chuyển' },
  },

  events: {
    when: 'Thời điểm',
    event: 'Sự kiện',
    actor: 'Thực hiện bởi',
    correlation: 'Correlation',
  },

  compliance: {
    decisions: 'Quyết định chính sách',
    decisionsDescription: 'Mọi quyết định tuân thủ đã đưa ra cho tên miền, kèm phiên bản chính sách đã áp dụng.',
    noDecisions: 'Chưa có quyết định nào',
    decided: 'Quyết định lúc',
    operation: 'Thao tác',
    outcome: 'Kết quả',
    actions: 'Yêu cầu xử lý / lý do chặn',
    policies: 'Chính sách',
    correlation: 'Correlation',
    abuse: 'Vụ việc lạm dụng',
    openAbuse: 'Mở trong Lạm dụng',
    noAbuse: 'Không có vụ việc lạm dụng',
    abuseLine: '{{category}} · ưu tiên {{priority}} · mở {{when}}',
    disputes: 'Tranh chấp và khoá pháp lý',
    openDisputes: 'Mở trong Tranh chấp',
    noDisputes: 'Không có tranh chấp',
    lockActive: 'đang áp dụng',
    lockReleased: 'đã gỡ',
    lockLine: '{{lock}} · {{state}}',
    categories: {
      SPAM: 'Thư rác',
      PHISHING: 'Lừa đảo giả mạo (phishing)',
      MALWARE: 'Mã độc',
      BOTNET: 'Mạng máy tính ma (botnet)',
      PHARMING: 'Chuyển hướng giả mạo (pharming)',
      FRAUD: 'Gian lận',
      CSAM: 'Nội dung xâm hại tình dục trẻ em',
      TRADEMARK: 'Xâm phạm nhãn hiệu',
      OTHER: 'Khác',
    },
    priorities: { LOW: 'thấp', MEDIUM: 'trung bình', HIGH: 'cao', CRITICAL: 'khẩn cấp' },
    disputeTypes: {
      UDRP: 'UDRP',
      URS: 'URS',
      COURT_ORDER: 'Quyết định của toà án',
      REGISTRY_ACTION: 'Yêu cầu của registry',
      GOVERNMENT_REQUEST: 'Yêu cầu của cơ quan nhà nước',
      OTHER: 'Tranh chấp khác',
    },
    requiredActions: {
      CREATE_DOSSIER: 'Lập hồ sơ tên miền',
      COMPLETE_DOSSIER: 'Hoàn thiện hồ sơ tên miền',
      VERIFY_SUBJECT_IDENTITY: 'Xác minh danh tính chủ thể',
      PROVIDE_DOCUMENT: 'Nộp giấy tờ',
      UPLOAD_DOCUMENT: 'Nộp giấy tờ',
    },
    blockReasons: {
      REGISTRANT_TYPE_NOT_ELIGIBLE: 'Loại chủ thể không đủ điều kiện',
      NATIONALITY_NOT_ELIGIBLE: 'Quốc tịch không đủ điều kiện',
      COUNTRY_NOT_ELIGIBLE: 'Quốc gia không đủ điều kiện',
      SUBJECT_MISSING: 'Chưa có chủ thể',
      LEGAL_LOCK: 'Đang có khoá pháp lý',
    },
    acceptedMethods: 'chấp nhận: {{methods}}',
    underPolicy: 'theo {{policy}}',
  },

  vnnic: {
    title: 'VNNIC',
    description: 'Nghĩa vụ với VNNIC của tên miền này.',
    program: 'Chương trình',
    programVn: 'Đồng bộ hồ sơ (.vn)',
    programIntl: 'Báo cáo tên miền quốc tế (0805A / 0805B), nếu chủ thể là người/tổ chức Việt Nam',
    dossier: 'Hồ sơ',
    openDossier: 'Mở hồ sơ',
    note: 'Trạng thái đồng bộ và báo cáo của mọi tên miền nằm ở các trang VNNIC bên dưới.',
    dossierSync: 'Đồng bộ hồ sơ',
    intlReports: 'Báo cáo quốc tế',
    coverage: 'Độ phủ / sai lệch',
    sync: 'Đồng bộ hồ sơ với VNNIC',
    syncDescription: 'Lần gửi hồ sơ gần nhất của tên miền này và phản hồi của VNNIC.',
    syncLoading: 'Đang tải trạng thái đồng bộ…',
    syncFailed: 'Không tải được trạng thái đồng bộ',
    noSync: 'Hồ sơ của tên miền này chưa được gửi tới VNNIC.',
    syncStatus: 'Trạng thái đồng bộ',
    syncOperation: 'Loại gửi',
    operations: { SUBMIT: 'Gửi lần đầu', UPDATE: 'Cập nhật hồ sơ' },
    dossierVersion: 'Phiên bản hồ sơ',
    attempts: 'Số lần gửi',
    reference: 'Mã tham chiếu VNNIC',
    changedAt: 'Trạng thái từ',
    nextAttempt: 'Lần gửi tiếp theo',
    lastAttempt: 'Lần gửi gần nhất',
    attemptResult: 'Kết quả',
    submittedAt: 'Gửi lúc',
    completedAt: 'Phản hồi lúc',
    errorCode: 'Lý do',
    errorMessage: 'Thông báo của VNNIC',
  },

  history: {
    requestedBy: 'theo yêu cầu của',
    queue: {
      title: 'Thao tác đang chờ',
      loading: 'Đang tải hàng đợi…',
      empty: 'Không có thao tác nào đang chờ.',
      running: 'Đang chạy',
      step: 'Bước',
      next: 'Chạy tiếp lúc {{when}}',
      scheduledBadge: 'Đã hẹn',
      scheduled: 'Chạy lúc {{when}}',
      actions: {
        DOMAIN_RENEW_FLAG_RELEASE: 'Gỡ cờ clientRenewProhibited (24 giờ sau gia hạn)',
        DOMAIN_RECONCILE: 'Đọc lại tên miền tại registry',
      },
    },
    title: 'Lịch sử',
    description: 'Toàn bộ sự kiện của tên miền theo thứ tự, được bảo vệ bằng chuỗi băm.',
    loading: 'Đang tải lịch sử…',
    loadFailed: 'Không tải được lịch sử',
    filter: 'Lọc',
    filterPlaceholder: 'Loại sự kiện hoặc correlation ID',
    intact: 'Chuỗi băm nguyên vẹn: mã băm của mọi sự kiện khớp với nội dung và sự kiện liền trước.',
    broken: 'Chuỗi băm bị đứt tại {{count}} sự kiện',
    brokenBody: 'Lịch sử có thể đã bị sửa ngoài ứng dụng. Chuyển ngay cho bộ phận an ninh; không chỉnh sửa các dòng dữ liệu.',
    noEvents: 'Chưa có sự kiện',
    noEventsFiltered: 'Không có sự kiện khớp bộ lọc. Xoá bộ lọc để xem mọi sự kiện.',
    mismatch: 'Sai mã băm',
    by: 'bởi {{actor}}',
    via: 'qua {{source}}',
    payload: 'Nội dung sự kiện',
    correlation: 'Correlation',
    workflow: 'Quy trình',
    registryTx: 'Lệnh registry',
    registryTxTrid: 'Lệnh registry {{cltrid}}',
    request: 'Mã yêu cầu',
    chainProblem: {
      SEQUENCE_GAP: 'thiếu sự kiện ngay trước sự kiện này (số thứ tự bị đứt quãng)',
      PREVIOUS_HASH_MISMATCH: 'liên kết tới sự kiện liền trước không khớp',
      EVENT_HASH_MISMATCH: 'mã băm đã lưu không khớp với nội dung sự kiện',
      other: 'kiểm tra mã băm không đạt',
    },
    olderFailed: 'Không tải được sự kiện cũ hơn',
    loadOlder: 'Tải sự kiện cũ hơn',
    loadingOlder: 'Đang tải…',
  },

  audit: {
    title: 'Nhật ký kiểm toán',
    description: 'Mọi thao tác được kiểm toán trên tên miền này, mới nhất trước.',
    loadFailed: 'Không tải được nhật ký kiểm toán',
    empty: 'Chưa có mục nhật ký nào cho tên miền này',
    when: 'Thời điểm',
    action: 'Thao tác',
    actor: 'Thực hiện bởi',
    outcome: 'Kết quả',
    reason: 'Lý do',
    correlation: 'Correlation',
    outcomes: { SUCCESS: 'Thành công', DENIED: 'Bị từ chối', FAILED: 'Thất bại', FAILURE: 'Thất bại', ERROR: 'Lỗi' },
    older: 'Mục cũ hơn',
  },

  observability: {
    severities: { LOW: 'Thấp', MEDIUM: 'Trung bình', HIGH: 'Cao', CRITICAL: 'Nghiêm trọng' },
    issueTypes: {
      UNKNOWN_OUTCOME: 'Chưa rõ kết quả lệnh registry',
      EXISTENCE: 'Tên miền chỉ có ở một phía',
      SPONSORSHIP: 'Nhà đăng ký quản lý không khớp',
      NAMESERVER: 'Máy chủ tên khác với registry',
      EXPIRY: 'Ngày hết hạn khác với registry',
      STATUS: 'Trạng thái khác với registry',
    },
    jump: 'Chuyển đến',
    jumpDescription: 'Truy vấn log có nêu tên miền cần ghi lý do; lý do được lưu cùng truy vấn.',
    logsMentioning: 'Log có nhắc đến {{name}}',
    latestLogs: 'Log của thao tác gần nhất',
    registryHealth: 'Tình trạng {{registry}}',
    loading: 'Đang tải liên kết vận hành…',
    loadFailed: 'Không tải được liên kết vận hành',
    issues: 'Vấn đề đối soát đang mở',
    noIssues: 'Không có vấn đề đối soát nào đang mở.',
    detected: 'phát hiện {{when}}',
    workflows: 'Workflow',
    noWorkflows: 'Không có workflow',
    updated: 'Cập nhật',
    workflow: 'Workflow',
    state: 'Trạng thái',
    step: 'Bước',
    correlation: 'Correlation',
    txs: 'Lệnh registry',
    noTxs: 'Chưa có lệnh registry',
    started: 'Bắt đầu',
    operation: 'Lệnh',
    events: 'Sự kiện gần đây',
    noEvents: 'Chưa có sự kiện',
    when: 'Thời điểm',
    event: 'Sự kiện',
    audited: 'Mọi lần tra cứu trên thẻ này đều được ghi vào nhật ký truy vấn.',
  },

  subject: {
    sync: {
      title: 'Tên miền dùng chủ thể này',
      description: 'Liên hệ của các tên miền được sao chép từ chủ thể này. Sửa chủ thể không tự thay đổi tên miền: chọn tên miền để đồng bộ.',
      loadFailed: 'Không tải được danh sách tên miền',
      empty: 'Chưa có tên miền nào dùng chủ thể này',
      domain: 'Tên miền',
      contacts: 'Liên hệ',
      changes: 'Khác biệt',
      statuses: { CURRENT: 'Đã khớp', BEHIND: 'Theo thông tin cũ', EDITED: 'Đã sửa riêng', SYNCING: 'Đang đồng bộ' },
      select: 'Chọn {{domain}}',
      action: 'Đồng bộ sang tên miền',
      actionCount: 'Đồng bộ sang {{count}} tên miền',
      dialogTitle: 'Đồng bộ sang tên miền',
      dialogDescription:
        'Liên hệ của các tên miền đã chọn nhận thông tin hiện tại của chủ thể; mỗi tên miền được cập nhật riêng tại registry. Nếu tên hoặc số giấy tờ đã đổi, chủ thể của tên miền được đổi qua yêu cầu đổi chủ thể hoặc đổi tên.',
      editedWarning: 'Có tên miền đã sửa liên hệ riêng: đồng bộ sẽ thay bằng thông tin của chủ thể.',
      submit: 'Đồng bộ',
      results: {
        STARTED: 'Đang cập nhật {{roles}}',
        REGISTRANT_CHANGE: 'Đã mở yêu cầu đổi chủ thể',
        PENDING_CONFIRMATION: 'Chờ nhân viên xác nhận thay đổi',
        NAME_CHANGE: 'Đã mở yêu cầu đổi tên',
        UP_TO_DATE: 'Đã khớp, không cần cập nhật',
        SKIPPED: 'Không thực hiện: {{reason}}',
      },
    },
    readiness: {
      title: 'Đủ thông tin để đăng ký tên miền',
      description: 'Thông tin chủ thể còn thiếu theo quy định chung cho mọi tên miền, rồi phần một nhà quản lý tên miền hoặc nhà đăng ký cấp trên cần thêm: kiểm tra giống lúc báo giá và đặt hàng. Xác minh chủ thể không được kiểm tra khi đặt hàng: đơn đã đặt chờ ở bước "Chờ định danh" cho tới khi chủ thể được xác minh.',
      missing: 'Thiếu {{count}} thông tin',
      ready: 'Chủ thể đã đủ thông tin để đăng ký tên miền.',
      missingTitle: 'Còn thiếu để đăng ký tên miền',
      extraFor: '{{name}} cần thêm:',
      verification: {
        label: 'Xác minh chủ thể',
        EKYC: 'Cá nhân chỉ được xác minh bằng eKYC',
        SUBJECT_DECLARATION: 'Tổ chức xác minh bằng bản khai chủ thể (ký số, hoặc bản giấy ký tên, đóng dấu)',
        states: {
          MISSING: 'Chưa xác minh',
          NOT_ACCEPTED: 'Chưa xác minh (cần eKYC): bản khai ký số không còn được chấp nhận cho cá nhân',
          SIGN_AGAIN: 'Thông tin đã thay đổi: cần ký lại bản khai chủ thể',
          PENDING: 'Bản khai chủ thể đang chờ kiểm tra hoặc nhân viên duyệt',
        },
        open: 'Mở bản khai chủ thể',
      },
    },
    statuses: { ACTIVE: 'Hoạt động', INACTIVE: 'Ngừng hoạt động', SUSPENDED: 'Tạm ngưng', MERGED: 'Đã hợp nhất', DELETED: 'Đã xoá' },
    types: {
      INDIVIDUAL: 'Cá nhân',
      ORGANIZATION: 'Tổ chức',
      ORGANISATION: 'Tổ chức',
      BUSINESS: 'Doanh nghiệp',
      HOUSEHOLD_BUSINESS: 'Hộ kinh doanh',
      GOVERNMENT_ENTITY: 'Cơ quan nhà nước',
      FOREIGN_ENTITY: 'Tổ chức nước ngoài',
      OTHER: 'Loại khác',
    },
    verificationStatuses: { VERIFIED: 'Đã xác minh', PENDING: 'Chờ xác minh', IN_PROGRESS: 'Đang xác minh', FAILED: 'Thất bại', REJECTED: 'Bị từ chối', EXPIRED: 'Hết hạn' },
    identifierTypes: {
      NATIONAL_ID: 'CCCD',
      TAX_CODE: 'Mã số thuế',
      ENTERPRISE_CODE: 'Mã số doanh nghiệp',
      HOUSEHOLD_BUSINESS_CODE: 'Mã số đăng ký hộ kinh doanh',
      BUSINESS_HOUSEHOLD_TAX_CODE: 'Mã số thuế hộ kinh doanh',
      PASSPORT: 'Hộ chiếu',
      CCCD: 'Căn cước công dân (CCCD)',
      CMND: 'Chứng minh nhân dân (CMND)',
      BUSINESS_REGISTRATION: 'Đăng ký kinh doanh',
      TAX_ID: 'Mã số thuế',
      BIRTH_DATE: 'Ngày sinh',
    },
    genders: { MALE: 'Nam', FEMALE: 'Nữ' },
    relationships: {
      LEGAL_REPRESENTATIVE: 'Người đại diện theo pháp luật',
      GUARDIAN: 'Người giám hộ',
      AUTHORIZED_REPRESENTATIVE: 'Người được ủy quyền',
      PARENT_ORGANIZATION: 'Tổ chức mẹ',
      OTHER: 'Khác',
    },
    addressTypes: { LEGAL: 'Địa chỉ pháp lý', POSTAL: 'Địa chỉ liên lạc', RESIDENTIAL: 'Nơi cư trú', OTHER: 'Địa chỉ khác' },
    profile: {
      title: 'Thông tin đã lưu',
      description: 'Toàn bộ dữ liệu của phiên bản hiện tại. Ngày sinh và số định danh được che; muốn xem cần nêu lý do và được ghi nhật ký kiểm toán.',
      personal: 'Cá nhân',
      organisation: 'Tổ chức',
      contact: 'Liên hệ',
      address: 'Địa chỉ',
      attribute: {
        first_name: 'Tên',
        last_name: 'Họ',
        contact_first_name: 'Tên người liên hệ',
        contact_last_name: 'Họ người liên hệ',
        owner_name: 'Chủ hộ kinh doanh',
        household_code: 'Mã số hộ kinh doanh',
        id_document_issuer: 'Nơi cấp giấy tờ',
      },
      birthDate: 'Ngày sinh',
      birthDateMissing: 'Chưa lưu',
      gender: 'Giới tính',
      position: 'Chức vụ',
      tradeName: 'Tên giao dịch',
      street: 'Số nhà, đường',
      ward: 'Phường/xã',
      province: 'Tỉnh/thành phố',
      city: 'Thành phố',
      state: 'Bang/tỉnh',
      postalCode: 'Mã bưu chính',
      fullAddress: 'Địa chỉ đầy đủ',
      email: 'E-mail',
      phone: 'Điện thoại',
      fax: 'Fax',
      issuedAt: 'ngày cấp {{date}}',
      issuedBy: 'nơi cấp: {{country}}',
      expiresAt: 'hết hạn {{date}}',
      otherAttributes: 'Thông tin khác đã lưu',
      relationships: 'Quan hệ',
      since: 'từ {{date}}',
      versionReason: 'Lý do của phiên bản',
      none: 'Chưa có',
    },
    methods: {
      EKYC: 'eKYC (VNNIC Cloud eKYC)',
      DIGITAL_SIGNATURE: 'Tài liệu ký số',
      EKYC_CCCD_CHIP: 'eKYC bằng CCCD gắn chip',
      BUSINESS_REGISTRY_LOOKUP: 'Tra cứu cơ sở dữ liệu doanh nghiệp',
      MANUAL_REVIEW: 'Duyệt thủ công',
      VIDEO_CALL: 'Gọi video',
    },
    loading: 'Đang tải chủ thể…',
    notFound: 'Không tìm thấy chủ thể',
    loadFailed: 'Không tải được chủ thể',
    fallbackTitle: 'Chủ thể',
    description: 'Chủ thể pháp lý đứng tên tên miền. Số định danh được che; mỗi lần hiển thị đều được ghi nhật ký kiểm toán.',
    type: 'Loại',
    status: 'Trạng thái',
    id: 'ID chủ thể',
    currentVersion: 'Phiên bản hiện tại',
    country: 'Quốc gia',
    nationality: 'Quốc tịch',
    managingCustomer: 'Khách hàng quản lý',
    identifiers: 'Số định danh',
    identifiersDescription: 'Hiển thị dạng che. Muốn xem cần nêu lý do và được ghi vào nhật ký kiểm toán.',
    noIdentifiers: 'Phiên bản hiện tại không có số định danh.',
    verifications: 'Xác minh',
    noVerifications: 'Chưa có xác minh',
    noVerificationsBody: 'Chủ thể này chưa được xác minh danh tính.',
    method: 'Phương thức',
    verified: 'Xác minh lúc',
    expires: 'Hết hạn',
    policy: 'Chính sách',
    requestId: 'Mã yêu cầu (request_id)',
    versionCol: 'Cho phiên bản',
    versions: 'Lịch sử phiên bản',
    version: 'Phiên bản',
    legalName: 'Tên pháp lý',
    changeReason: 'Lý do thay đổi',
    changeReasons: { INITIAL: 'Phiên bản đầu tiên', NAME_CHANGE: 'Đổi tên', CORRECTION: 'Đính chính', ADDRESS_CHANGE: 'Đổi địa chỉ', CREATED_BY_STAFF: 'Nhân viên tạo mới', EKYC: 'eKYC', DIGITAL_SIGNATURE: 'Ký số' },
    created: 'Tạo lúc',
    domains: 'Tên miền đang đứng tên',
    domainsDescription: 'Các tên miền có chủ thể hiện tại là chủ thể này.',
    noDomains: 'Chủ thể này không đứng tên tên miền nào.',
    domain: 'Tên miền',
    lifecycle: 'Vòng đời',
    dossierState: 'Hồ sơ',
    expiry: 'Hết hạn tại registry',
    customer: 'Khách hàng',
    dossiers: 'Hồ sơ',
    noDossiers: 'Không có hồ sơ nào gắn với chủ thể này.',
    dossierVersion: 'Phiên bản',
    dossierType: 'Loại',
    dossierStatus: 'Trạng thái',
    subjectVersion: 'Phiên bản chủ thể',
    completed: 'Hoàn tất lúc',
    contacts: 'Liên hệ tại registry',
    contactsDescription: 'Các đối tượng liên hệ tại registry được tạo từ chủ thể này.',
    noContacts: 'Chưa có đối tượng liên hệ nào được tạo từ chủ thể này.',
    contact: 'Liên hệ',
    registry: 'Registry',
    roles: 'Vai trò hiện tại',
    contactDomains: 'Tên miền',
    pageTitle: 'Chủ thể',
    pageSubtitle: 'Thông tin chủ thể, xác minh, tên miền đứng tên và lịch sử phiên bản.',
    crumbBusiness: 'Kinh doanh',
    crumbSubjects: 'Chủ thể',
    docTitle: 'Chủ thể {{name}}',
  },

  registryTx: {
    openTrace: 'Mở trace',
    title: 'Lệnh registry',
    docTitle: 'Lệnh registry {{operation}}',
    crumbSection: 'Vận hành Registry',
    crumbList: 'Giao dịch registry',
    notFound: 'Không tìm thấy lệnh registry',
    loadFailed: 'Không tải được lệnh registry',
    subtitle: 'Một lệnh EPP gửi tới {{registry}}: định danh, kết quả và nơi theo dấu tiếp.',
    unresolvedTitle: 'Chưa rõ kết quả',
    unresolved: 'Quá trình đối soát sẽ xác định kết quả dựa trên dữ liệu registry; không gửi lại lệnh thủ công.',
    command: 'Lệnh',
    state: 'Trạng thái',
    attempt: 'Lần thử',
    retryOf: 'Thử lại của',
    retryOfLink: 'lần thử trước',
    object: 'Đối tượng',
    domain: 'Tên miền',
    result: 'Kết quả',
    errorClass: 'Nhóm lỗi',
    trafficClass: 'Nhóm lưu lượng',
    registry: 'Registry',
    started: 'Bắt đầu',
    sent: 'Gửi lúc',
    completed: 'Hoàn tất',
    latency: 'Độ trễ',
    none: 'không có',
    id: 'ID lệnh',
    follow: 'Theo dấu',
    followDescription: 'Chuyển đến workflow, log và trace liên quan.',
    correlation: 'Correlation',
    workflow: 'Workflow',
    request: 'Mã yêu cầu',
    logs: 'Log của thao tác này',
    traces: 'Trace của thao tác này',
    eppLogs: 'Log lệnh EPP theo clTRID',
    payloads: 'Nội dung trao đổi EPP',
    withheld: 'Đã ẩn. Xem nội dung EPP (đã che dữ liệu nhạy cảm) cần một quyền riêng, và mọi lần xem đều được ghi lại.',
    redacted: 'Dữ liệu được lưu ở dạng đã che. Lần xem này đã được ghi vào nhật ký kiểm toán.',
    requestPayload: 'Yêu cầu',
    responsePayload: 'Phản hồi',
    payloadsDescription:
      'Yêu cầu và phản hồi đúng như đã gửi và nhận (đã che mật khẩu, AuthInfo và số định danh), lấy từ kho log theo clTRID và giải mã trên máy chủ. Mỗi lần xem đều được ghi vào nhật ký kiểm toán.',
    summary: 'Tóm tắt lưu cùng lệnh',
    exchange: {
      title: 'Nguyên văn trao đổi',
      loading: 'Đang đọc kho log…',
      loadFailed: 'Không đọc được nội dung trao đổi từ kho log',
      storeOff: 'Kho log chưa được kết nối nên chưa xem được nội dung đã trao đổi. Phần tóm tắt phía trên vẫn xem được.',
      reasonIntro: 'Nguyên văn trao đổi có dữ liệu liên hệ của chủ thể. Nêu lý do xem; lý do được ghi kiểm toán cùng lượt xem.',
      mfa: 'Xem nội dung đã trao đổi cần phiên đăng nhập có xác thực MFA. Hãy đăng nhập lại kèm MFA.',
      expiredTitle: 'Đã quá thời hạn lưu',
      expired: 'Log trao đổi với registry được giữ {{days}} ngày; lệnh này chạy cách đây {{age}} ngày nên nội dung đã được dọn.',
      retentionSetting: 'Cài đặt lưu trữ log',
      noneTitle: 'Chưa có bản ghi trao đổi cho clTRID này',
      none: 'Lệnh chạy trước khi bật ghi log trao đổi, chưa được gửi đi, hoặc log chưa kịp chuyển vào kho.',
      request: 'Yêu cầu',
      response: 'Phản hồi',
      noResponse: 'Không có phản hồi đọc được (hết thời gian chờ, mất kết nối hoặc chưa gửi).',
      copy: 'Sao chép nguyên văn',
      copied: 'Đã sao chép',
      session: 'Phiên',
      at: 'Ghi lúc',
      result: { success: 'Thành công', rejected: 'Bị từ chối', unknown: 'Chưa rõ kết quả', error: 'Không gửi được' },
      payload: {
        absent: 'Lượt trao đổi này không có nội dung được ghi lại.',
        no_field_key: 'Không có nội dung: dịch vụ thiếu khoá mã hoá trường khi ghi hoặc khi đọc log.',
        too_large: 'Nội dung quá lớn so với một dòng log nên không được lưu.',
        seal_failed: 'Không mã hoá được nội dung khi ghi log nên nội dung không được lưu.',
        unreadable: 'Không giải mã được nội dung (khoá đã đổi hoặc dòng log bị hỏng).',
      },
    },
  },

  search: {
    title: 'Tìm kiếm',
    docTitle: 'Tìm kiếm: {{q}}',
    subtitle: 'Tra cứu tên miền, khách hàng, chủ thể hoặc mã định danh bất kỳ trên toàn nền tảng.',
    empty: 'Nhập nội dung vào ô tìm kiếm phía trên.',
    interpreted: 'Cách hiểu truy vấn',
    interpretedHint: 'Truy vấn được nhận dạng theo hình thức, kèm lối tắt cho từng cách hiểu.',
    confidence: { high: 'Độ tin cậy cao', medium: 'Độ tin cậy trung bình', low: 'Độ tin cậy thấp' },
    results: 'Kết quả',
    searching: 'Đang tìm…',
    failed: 'Tìm kiếm thất bại',
    noMatches: 'Không có kết quả',
    noMatchesBody: 'Không có kết quả cho {{q}}. ID, clTRID, svTRID và mã liên hệ phải khớp chính xác; tên cần ít nhất 3 ký tự.',
    correlation: 'Nếu đây là correlation ID, {{link}} hiển thị mọi sự kiện, workflow, lệnh registry và bản ghi kiểm toán liên quan.',
    correlationLink: 'trang Correlation',
    accepts: 'Có thể tìm theo',
    acceptsList: {
      domains: 'Tên miền, dạng Unicode (ví-dụ.vn) hoặc Punycode (xn--v-d-rma6749a.vn)',
      names: 'Tên khách hàng và tên chủ thể (từ 3 ký tự)',
      contacts: 'Mã liên hệ tại registry, ví dụ TINO-C123456',
      trids: 'clTRID / svTRID của EPP',
      ids: 'ID bản ghi, request, correlation, trace và workflow',
    },
    shortcut: 'Nhấn {{key}} ở bất kỳ đâu để chuyển đến ô tìm kiếm.',
    kinds: {
      domain: 'Tên miền',
      domain_event: 'Sự kiện',
      registry_transaction: 'Lệnh registry',
      workflow: 'Workflow',
      subject: 'Chủ thể',
      customer: 'Khách hàng',
      transfer: 'Chuyển nhà ĐK',
      registrant_change: 'Đổi chủ thể',
      dossier: 'Hồ sơ',
      contact: 'Liên hệ',
    },
    hit: {
      domainState: 'Tên miền · {{state}}',
      event: '{{domain}} · sự kiện lịch sử khớp theo request hoặc correlation ID',
      registryTx: 'Lệnh registry · {{detail}}',
      workflow: 'Workflow · {{detail}}',
      subject: 'Chủ thể · {{type}}',
      subjectHidden: 'Chủ thể ({{type}}), đã ẩn tên',
      customer: 'Khách hàng · {{type}}',
      transfer: '{{direction}} · {{status}}',
      registrantChange: 'Đổi chủ thể · {{status}}',
      dossier: 'Hồ sơ {{version}} · {{status}}',
      contact: 'Liên hệ tại {{registry}}',
    },
  },
  /** "Tra cứu tên miền" (docs/specs/external-lookup.md). */
  lookup: {
    title: 'Tra cứu tên miền',
    subtitle: 'Tên còn trống ở nhiều TLD cùng lúc và ai đang giữ tên: ưu tiên kết nối registry của Tino, sau đó đến máy chủ WHOIS / RDAP công khai của registry.',
    label: 'Tên hoặc nhãn',
    labelHint: 'Nhãn (tino) được kiểm tra ở mọi TLD đã chọn; tên đầy đủ (tino.vn) thêm TLD của nó.',
    tlds: 'TLD',
    tldsHint: 'Cách nhau bằng dấu cách hoặc dấu phẩy, tối đa 50 tên mỗi lần.',
    submit: 'Tra cứu',
    stop: 'Dừng',
    running: 'Đang tra cứu… kết quả hiện ngay khi từng nguồn trả lời.',
    tooMany: 'Tối đa 50 tên mỗi lần tra cứu.',
    empty: 'Nhập tên rồi bấm Tra cứu.',
    col: { name: 'Tên miền', status: 'Kết quả', source: 'Nguồn', age: 'Câu trả lời', time: 'Thời gian', actions: '' },
    status: { available: 'Còn trống', registered: 'Đã đăng ký', unknown: 'Không rõ', confirming: 'Đã đăng ký (đang xác nhận…)' },
    source: { REGISTRY: 'Registry (kết nối của Tino)', VNNIC_WHOIS_API: 'WHOIS của VNNIC', RDAP: 'RDAP', WHOIS: 'WHOIS', DNS: 'DNS (đã uỷ quyền)' },
    authoritative: 'Chính thức',
    publicSource: 'Máy chủ công khai',
    cached: 'Từ bộ nhớ đệm, cách đây {{age}}',
    fresh: 'Mới',
    premium: 'Tên cao cấp: {{amount}} {{currency}}',
    reason: {
      INVALID_NAME: 'Tên miền không hợp lệ',
      TLD_NOT_SUPPORTED: 'TLD không có trong danh mục',
      SOURCE_NOT_CONFIGURED: 'Chưa cấu hình WHOIS VNNIC trên máy chủ này (chưa nhập chứng chỉ)',
      NO_SOURCE: 'Không có nguồn tra cứu cho TLD này (đã tắt tra cứu hoặc không có máy chủ công khai)',
      TIMEOUT: 'Nguồn không trả lời kịp',
      RATE_LIMITED: 'Đã tra cứu quá nhiều ở máy chủ này; thử lại sau ít phút',
      SOURCE_ERROR: 'Nguồn trả lời lỗi',
      DELEGATED: 'Đã được uỷ quyền trong DNS',
      RDAP_NOT_FOUND: 'RDAP: không tìm thấy',
      WHOIS_NOT_FOUND: 'WHOIS: không có bản ghi',
    },
    notice: 'Kết quả từ máy chủ công khai (RDAP, WHOIS, WHOIS của VNNIC) là theo máy chủ công khai của registry — chưa phải xác nhận đăng ký được: tên có thể bị giữ, là tên cao cấp hoặc bị chặn. Chỉ kết nối registry của Tino là chính thức.',
    openRecord: 'WHOIS / RDAP',
    record: {
      notConfigured: {
        title: 'Chưa cấu hình WHOIS VNNIC',
        body: 'Tên miền .vn chỉ tra được thông tin đăng ký qua dịch vụ WHOIS của VNNIC. Máy chủ này chưa nhập chứng chỉ WHOIS của VNNIC nên chưa tra được.',
        action: 'Nhập chứng chỉ WHOIS VNNIC',
      },
      title: 'Thông tin đăng ký của {{name}}',
      source: 'Nguồn',
      server: 'Máy chủ',
      found: 'Đã đăng ký',
      notFound: 'Nguồn cho biết tên này chưa được đăng ký.',
      status: 'Trạng thái',
      registrar: 'Nhà đăng ký',
      created: 'Ngày đăng ký',
      expires: 'Ngày hết hạn',
      nameservers: 'Máy chủ tên',
      dnssec: 'DNSSEC',
      registrant: 'Chủ thể',
      registrantHidden: 'Đã ẩn: cần quyền “Xem tên chủ thể khi tra cứu WHOIS tại nhà quản lý tên miền” để xem tên chủ thể. Tên chủ thể không bao giờ được lưu lại.',
      raw: 'Câu trả lời gốc',
      cached: 'Từ bộ nhớ đệm ({{age}})',
      failed: 'Không đọc được thông tin đăng ký',
    },
    commandItem: 'Tra cứu “{{q}}” ở các TLD',
  },
  extra: {
    heading: 'Thông tin bổ sung theo yêu cầu của đuôi .{{zone}}',
    intro: 'Đuôi tên miền này cần thêm một số thông tin ngoài hồ sơ chủ thể. Điền cho tên miền này.',
    required: 'bắt buộc',
    requiredIndividuals: 'bắt buộc với cá nhân',
    requiredOrganizations: 'bắt buộc với tổ chức',
    optional: 'không bắt buộc',
    choose: 'Chọn…',
    yes: 'Có',
    no: 'Không',
    fromSubject: 'Để trống để dùng thông tin của chủ thể ({{hint}}).',
    fromSubjectPlain: 'Đã điền sẵn từ thông tin của chủ thể.',
    fromEarlier: 'Đã điền sẵn theo thông tin chủ thể đã khai cho một tên miền .{{zone}} khác.',
    fromEarlierHidden: 'Để trống để dùng lại thông tin chủ thể đã khai trước đây ({{hint}}).',
    missing: 'Còn thiếu: {{fields}}',
    loadFailed: 'Không tải được thông tin bổ sung của đuôi tên miền này.',
    sectionHint: 'Lưu riêng cho tên miền này và được gửi tới nhà đăng ký cùng thông tin chủ thể.',
    notSet: 'Chưa có',
    hidden: 'đã ẩn',
    sources: {
      GIVEN: 'Nhập trực tiếp',
      EARLIER: 'Lấy từ tên miền khác của chủ thể',
      SUBJECT: 'Lấy từ thông tin chủ thể',
    },
    by: {
      OPERATOR: 'nhân viên',
      CUSTOMER: 'khách hàng',
      RESELLER: 'đại lý',
      SYSTEM: 'hệ thống',
      EXTERNAL: 'bên ngoài',
    },
    edit: 'Sửa',
    editTitle: 'Thông tin bổ sung của {{domain}}',
    editDescription:
      'Chỉ áp dụng cho tên miền này. Giá trị mới được gửi tới nhà đăng ký cùng thông tin chủ thể ở lần gửi tiếp theo (đăng ký chưa gửi hoặc thử lại, đổi chủ thể); thông tin đã gửi trước đó không tự thay đổi.',
    keepHidden: 'Để trống để giữ giá trị hiện tại.',
    save: 'Lưu',
    saved: 'Đã lưu.',
    todoLink: 'Nhập thông tin bổ sung',
  },
};
